import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { EpicService } from 'src/app/private/services/epic.service';
import { ProjectFeatureService } from 'src/app/private/services/project-feature.service';
import { UserStoryService } from 'src/app/private/services/user-story.service';
import { ProjectService } from 'src/app/private/services/project.service';
import { ProjectTaskService } from 'src/app/private/services/project-task.service';
import { BugService } from 'src/app/private/services/bug.service';
import { UsersService } from 'src/app/private/services/users.service';
import { SprintService } from 'src/app/private/services/sprint.service';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { ProjectDetailTasksComponent } from './project-detail-tasks/project-detail-tasks.component';
import { ProjectDetailKanbanComponent } from './project-detail-kanban/project-detail-kanban.component';
import { ProjectDetailBugsComponent } from './project-detail-bugs/project-detail-bugs.component';
import { ProjectDetailSprintComponent } from './project-detail-sprint/project-detail-sprint.component';

@Component({
  selector: 'app-project-detail',
  templateUrl: './project-detail.component.html',
  styleUrls: ['./project-detail.component.scss'],
})
export class ProjectDetailComponent implements OnInit {
  @ViewChild(ProjectDetailTasksComponent)
  tasksChild!: ProjectDetailTasksComponent;
  @ViewChild(ProjectDetailKanbanComponent)
  kanbanChild!: ProjectDetailKanbanComponent;
  @ViewChild(ProjectDetailBugsComponent) bugsChild!: ProjectDetailBugsComponent;
  @ViewChild(ProjectDetailSprintComponent)
  sprintChild!: ProjectDetailSprintComponent;
  @ViewChild('bugModal') bugModal: any;
  @ViewChild('storyModal') storyModal: any;

  projectId!: string;
  projectData: any = null;

  // ─── CKEditor ─────────────────────────────────────────────────────────────
  public Editor = ClassicEditor as unknown as any;
  editorConfig: any = {
    toolbar: {
      items: [
        'bold',
        'italic',
        'underline',
        '|',
        'numberedList',
        'bulletedList',
        '|',
        'link',
        'blockQuote',
        '|',
        'undo',
        'redo',
      ],
      shouldNotGroupWhenFull: true,
    },
    placeholder: 'Enter description here...',
  };

  // ─── Hierarchy Tree State ──────────────────────────────────────────────────
  epics: any[] = [];
  expandedEpics: Set<number> = new Set();
  expandedFeatures: Set<number> = new Set();

  // ─── Selection State ───────────────────────────────────────────────────────
  selectedEpic: any = null;
  selectedFeature: any = null;
  selectedUserStory: any = null;
  selectedItem: any = null;

  // ─── Main Tab State ────────────────────────────────────────────────────────
  activeMainTab:
    | 'overview'
    | 'backlog'
    | 'sprint'
    | 'kanban'
    | 'tasks'
    | 'bugs'
    | 'timeline'
    | 'calendar'
    | 'reports'
    | 'team'
    | 'modules'
    | 'settings' = 'overview';
  allProjectTasks: any[] = [];
  loadingAllTasks = false;
  allProjectBugs: any[] = [];
  loadingAllBugs = false;

  get sprintOptions(): number[] {
    let maxSprint = 3;
    if (this.allProjectTasks && this.allProjectTasks.length > 0) {
      for (const t of this.allProjectTasks) {
        if (t.sprint_id && Number(t.sprint_id) > maxSprint) {
          maxSprint = Number(t.sprint_id);
        }
      }
    }
    const opts = [];
    for (let i = 1; i <= maxSprint + 1; i++) {
      opts.push(i);
    }
    return opts;
  }

  // ─── Overview Dashboard State ─────────────────────────────────────────────
  overviewLoading = false;
  overviewTimeRange = 'last_8_weeks';
  recentActivity: any[] = [];
  upcomingDeadlines: any[] = [];
  loadingOverview = false;
  progressChartWeeks: any[] = [];
  hoveredChartPoint: number | null = null;

  // ─── Timeline State ────────────────────────────────────────────────────────
  timelineDate: Date = new Date();
  expandedStories: Set<number> = new Set();
  timelineViewMode: 'month' | 'week' | 'year' = 'month';
  timelineSprintFilter = 'all';

  // ─── Calendar State ────────────────────────────────────────────────────────
  calendarDate: Date = new Date();
  calendarSprintFilter = 'all';

  // ─── Sprints & Backlog state ───────────────────────────────────────────────
  sprints: any[] = [];
  loadingSprints = false;
  projectStories: any[] = [];
  loadingProjectStories = false;
  selectedSprint: any = null;

  // ─── Settings Form State ───────────────────────────────────────────────────
  settingsName = '';
  settingsDesc = '';
  settingsStatus = 'todo';
  settingsManagerId: any = null;
  settingsStartDate = '';
  settingsDueDate = '';
  settingsSaving = false;

  get currentUserId() {
    return this.localStorage.getUserId();
  }

  get clientId() {
    return this.localStorage.getClientId();
  }

  // ─── Data State ───────────────────────────────────────────────────────────
  featuresMap: Map<number, any[]> = new Map();
  isAdmin = false;
  storiesMap: Map<number, any[]> = new Map();
  storyTasks: any[] = [];
  rawStoryTasks: any[] = [];
  backlogTaskSearch = '';
  backlogTaskStatusFilter = 'all';
  backlogOnlyMyTasks = false;

  storyBugs: any[] = [];
  rawStoryBugs: any[] = [];
  backlogBugSearch = '';
  backlogBugStatusFilter = 'all';
  backlogOnlyMyBugs = false;
  displayedTasksLimit = 15;
  displayedBugsLimit = 15;
  storyTasksPage = 1;
  storyTasksTotal = 0;
  storyTasksPageSize = 15;
  storyBugsPage = 1;
  storyBugsTotal = 0;
  storyBugsPageSize = 15;
  Math = Math;

  // ─── Project Members ─────────────────────────────────────────────────────
  projectMembers: any[] = [];
  dropdownSettings = {
    singleSelection: false,
    idField: 'item_id',
    textField: 'item_text',
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    itemsShowLimit: 3,
    allowSearchFilter: true,
  };
  memberOptions: any[] = [];
  selectedTaskAssigneeObjects: any[] = [];
  selectedBugAssigneeObjects: any[] = [];
  departments: any[] = [];
  modules: any[] = [];
  projectBugs: any[] = [];

  // ─── Loading State ────────────────────────────────────────────────────────
  loadingProject = false;
  loadingEpics = false;
  loadingFeatures: Set<number> = new Set();
  loadingStories: Set<number> = new Set();
  loadingStoryTasks = false;
  loadingStoryBugs = false;
  loadingRightPanel = false;
  btnLoading = false;

  // ─── Panel UI State ────────────────────────────────────────────────────────
  leftPanelCollapsed = false;
  hierarchySearch = '';

  // ─── Inline Status Dropdown State ─────────────────────────────────────────
  activeStatusDdl: { id: any; itemType: 'task' | 'bug' } | null = null;

  // ─── Form States ──────────────────────────────────────────────────────────
  formAction: 'Add' | 'Update' = 'Add';
  epicData: any = {};
  featureData: any = {};
  userStoryData: any = {};

  // Task Form
  taskFormAction: 'Add' | 'Update' = 'Add';
  taskData: any = {};
  currentTaskId: any = null;
  activeTaskTab = 1;
  detailsLoading = false;

  // Hierarchy cascades for task form
  formFeatures: any[] = [];
  formUserStories: any[] = [];
  selectedFormEpicId: any = null;
  selectedFormFeatureId: any = null;

  // Bug Form
  bugFormAction: 'Add' | 'Update' = 'Add';
  bugData: any = {};
  currentBugId: any = null;
  activeBugTab = 1;
  bugDetailsLoading = false;

  // ─── Attachments State ──────────────────────────────────────────────────
  showPreview = false;
  selectedIndex = 0;
  activePreviewAttachments: any[] = [];

  // ─── Right Panel Tab ─────────────────────────────────────────────────────
  rightPanelTab:
    | 'details'
    | 'comments'
    | 'activity'
    | 'timelogs'
    | 'subtasks'
    | 'steps'
    | 'internal_comments'
    | 'attachments' = 'comments';

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private localStorage: LocalStorageService,
    private projectService: ProjectService,
    private epicService: EpicService,
    private featureService: ProjectFeatureService,
    private userStoryService: UserStoryService,
    private taskService: ProjectTaskService,
    private bugService: BugService,
    private userService: UsersService,
    private sprintService: SprintService,
  ) {}

  ngOnInit(): void {
    this.isAdmin = this.localStorage.checkFullAccessForTableList(
      this.localStorage.getRole(),
    );
    const id = this.route.snapshot.paramMap.get('project_id');
    if (id) {
      this.projectId = id;
      this.loadProject();
      this.loadProjectMembers();
      this.loadModules();
      this.loadEpics();
      this.loadOverviewData();
      this.loadSprints();
      this.loadAllProjectStories();
    }
  }

  @HostListener('document:click')
  onDocumentClick() {
    this.activeStatusDdl = null;
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  INLINE STATUS DROPDOWN
  // ══════════════════════════════════════════════════════════════════════════
  toggleStatusDdl(id: any, itemType: 'task' | 'bug', event: Event) {
    event.stopPropagation();
    if (this.activeStatusDdl?.id === id) {
      this.activeStatusDdl = null;
    } else {
      this.activeStatusDdl = { id, itemType };
    }
  }

  isStatusDdlOpen(id: any, itemType: 'task' | 'bug'): boolean {
    return (
      this.activeStatusDdl?.id === id &&
      this.activeStatusDdl?.itemType === itemType
    );
  }

  quickUpdateTaskStatus(task: any, status: string, event: Event) {
    event.stopPropagation();
    this.activeStatusDdl = null;
    const prev = task.status;
    const prevP = task.project_task_status;
    task.status = status;
    task.project_task_status = status;
    this.taskService
      .updateProjectTaskStatus({
        where: { task_id: task.task_id },
        status,
        client_id: this.localStorage.getClientId(),
      })
      .subscribe({
        error: () => {
          task.status = prev;
          task.project_task_status = prevP;
        },
      });
    if (this.selectedItem?.task_id === task.task_id) {
      this.selectedItem.status = status;
    }
  }

  quickUpdateBugStatus(bug: any, status: string, event: Event) {
    event.stopPropagation();
    this.activeStatusDdl = null;
    const prev = bug.status;
    bug.status = status;
    this.bugService
      .updateBug({
        bug_id: bug.bug_id,
        where: { bug_id: bug.bug_id },
        status,
        client_id: this.localStorage.getClientId(),
      })
      .subscribe({
        error: () => {
          bug.status = prev;
        },
      });
    if (this.selectedItem?.bug_id === bug.bug_id) {
      this.selectedItem.status = status;
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  PROJECT
  // ══════════════════════════════════════════════════════════════════════════
  loadProject() {
    this.loadingProject = true;
    this.projectService
      .getProjectById({
        project_id: this.projectId,
        client_id: this.localStorage.getClientId(),
      })
      .subscribe({
        next: (res: any) => {
          this.projectData = res;
          this.initSettingsForm();
          this.loadingProject = false;
        },
        error: () => {
          this.loadingProject = false;
        },
      });
  }

  loadProjectMembers() {
    this.projectService
      .getProjectMembers({
        client_id: this.localStorage.getClientId(),
        project_id: this.projectId,
        user_id: this.localStorage.getUserId(),
      })
      .subscribe({
        next: (data: any) => {
          this.projectMembers =
            this.projectService.parseProjectMembersResponse(data);
          this.memberOptions = (this.projectMembers || []).map((user: any) => ({
            item_id: user.user_id,
            item_text:
              `${user.prefix || ''} ${user.fname || ''} ${user.lname || ''}`
                .replace(/\s+/g, ' ')
                .trim(),
          }));
        },
        error: () => {},
      });
  }

  loadDepartments() {
    this.userService
      .getAllDepartment({ client_id: this.localStorage.getClientId() })
      .subscribe({
        next: (raw: any) => {
          const list = raw.data ? raw.data : Array.isArray(raw) ? raw : [];
          this.departments = list.map((el: any) => ({
            key: el.department_id || el.dept_id,
            value: el.dept_name || el.name,
          }));
        },
        error: () => {},
      });
  }

  loadModules() {
    this.projectService
      .getProjectModuleList({
        client_id: this.localStorage.getClientId(),
        project_id: this.projectId,
      })
      .subscribe({
        next: (res: any) => {
          const list = res?.data
            ? res.data
            : Array.isArray(res)
              ? res
              : res?.rows
                ? res.rows
                : [];
          this.modules = list.map((el: any) => ({
            ...el,
            key: Number(el.module_id || el.id),
            value: el.module_name || el.name,
            description: el.description || '',
          }));
        },
        error: () => {},
      });
  }

  // ─── MODULE MANAGEMENT ─────────────────────────────────────────────────────
  moduleModalAction: 'Add' | 'Update' = 'Add';
  moduleFormData: any = { module_name: '', description: '' };
  currentModuleId: any = null;
  moduleModalLoading = false;

  openAddModuleModal(content: any) {
    this.moduleModalAction = 'Add';
    this.currentModuleId = null;
    this.moduleFormData = { module_name: '', description: '' };
    this.modalService.open(content, { centered: true, size: 'md' });
  }

  openEditModuleModal(content: any, mod: any) {
    this.moduleModalAction = 'Update';
    this.currentModuleId = mod.key || mod.module_id || mod.id;
    this.moduleFormData = {
      module_name: mod.value || mod.module_name || '',
      description: mod.description || '',
    };
    this.modalService.open(content, { centered: true, size: 'md' });
  }

  submitModule() {
    if (!this.moduleFormData.module_name?.trim()) {
      this.toastr.error('Module name is required');
      return;
    }
    this.moduleModalLoading = true;
    const isUpdate = this.moduleModalAction === 'Update';
    const payload: any = {
      project_id: this.projectId,
      client_id: this.localStorage.getClientId(),
      module_name: this.moduleFormData.module_name.trim(),
      description: this.moduleFormData.description || '',
      updated_by: this.localStorage.getUserId(),
      created_by: this.localStorage.getUserId(),
    };
    if (isUpdate) {
      payload.module_id = this.currentModuleId;
      payload.where = { module_id: this.currentModuleId };
    }

    const req = isUpdate
      ? this.projectService.updateProjectModule(payload)
      : this.projectService.createProjectModule(payload);

    req.subscribe({
      next: () => {
        this.moduleModalLoading = false;
        this.toastr.success(
          `Module ${isUpdate ? 'updated' : 'created'} successfully`,
        );
        this.modalService.dismissAll();
        this.loadModules();
      },
      error: (err: any) => {
        this.moduleModalLoading = false;
        this.toastr.error(err?.error?.message || 'Failed to save module');
      },
    });
  }

  deleteModule(mod: any) {
    const moduleId = mod.key || mod.module_id || mod.id;
    if (
      !confirm(
        `Are you sure you want to delete module "${mod.value || mod.module_name}"?`,
      )
    ) {
      return;
    }
    this.projectService
      .deleteProjectModule({
        module_id: moduleId,
        client_id: this.localStorage.getClientId(),
        project_id: this.projectId,
      })
      .subscribe({
        next: () => {
          this.toastr.success('Module deleted successfully');
          this.loadModules();
        },
        error: () => this.toastr.error('Failed to delete module'),
      });
  }

  loadProjectBugs() {
    this.bugService
      .getAllBugs({
        client_id: this.localStorage.getClientId(),
        project_id: this.projectId,
        limit: 1000,
        offset: 0,
      })
      .subscribe({
        next: (data: any) => {
          this.projectBugs = (data?.rows ?? []).map((el: any) => ({
            key: el.bug_id,
            value: el.bug_code + ' - ' + el.title,
          }));
        },
        error: () => {},
      });
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  EPICS
  // ══════════════════════════════════════════════════════════════════════════
  loadEpics() {
    this.loadingEpics = true;
    this.epicService
      .getProjectHierarchy({
        client_id: this.localStorage.getClientId(),
        project_id: this.projectId,
      })
      .subscribe({
        next: (res: any) => {
          const data = res?.rows || res?.data?.rows || res?.data || res || [];
          this.epics = (Array.isArray(data) ? data : []).map((ep: any) => ({
            ...ep,
            epic_id: Number(ep.epic_id),
          }));

          // Populate the maps and pre-expand all epics/features for a seamless UX
          this.epics.forEach((epic: any) => {
            this.expandedEpics.add(epic.epic_id);
            const features = epic.features || [];
            this.featuresMap.set(epic.epic_id, features);

            features.forEach((feat: any) => {
              this.expandedFeatures.add(feat.feature_id);
              const stories = feat.stories || [];
              this.storiesMap.set(feat.feature_id, stories);
              stories.forEach((story: any) => {
                this.expandedStories.add(story.user_story_id);
              });
            });
          });

          // Auto-select or refresh selected user story tasks/bugs
          if (this.selectedUserStory) {
            let foundStory: any = null;
            let foundEpic: any = null;
            let foundFeat: any = null;
            for (const epic of this.epics) {
              const features = this.featuresMap.get(epic.epic_id) || [];
              for (const feat of features) {
                const stories = this.storiesMap.get(feat.feature_id) || [];
                foundStory = stories.find(
                  (s: any) =>
                    s.user_story_id === this.selectedUserStory.user_story_id,
                );
                if (foundStory) {
                  foundEpic = epic;
                  foundFeat = feat;
                  break;
                }
              }
              if (foundStory) break;
            }
            if (foundStory) {
              this.selectStory(foundEpic, foundFeat, foundStory);
            }
          } else if (this.epics && this.epics.length > 0) {
            for (const epic of this.epics) {
              const features = this.featuresMap.get(epic.epic_id) || [];
              for (const feat of features) {
                const stories = this.storiesMap.get(feat.feature_id) || [];
                if (stories && stories.length > 0) {
                  this.selectStory(epic, feat, stories[0]);
                  break;
                }
              }
              if (this.selectedUserStory) break;
            }
          }

          this.loadingEpics = false;
        },
        error: () => {
          this.toastr.error('Failed to load project hierarchy');
          this.loadingEpics = false;
        },
      });
  }

  toggleEpic(epic: any) {
    const id = epic.epic_id;
    if (this.expandedEpics.has(id)) {
      this.expandedEpics.delete(id);
    } else {
      this.expandedEpics.add(id);
      if (!this.featuresMap.has(id)) this.loadFeatures(epic);
    }
  }
  isEpicExpanded(epicId: number) {
    if (this.hierarchySearch && this.hierarchySearch.trim()) return true;
    return this.expandedEpics.has(epicId);
  }

  isEpicMatchingSearch(epic: any): boolean {
    if (!this.hierarchySearch || !this.hierarchySearch.trim()) return true;
    const term = this.hierarchySearch.trim().toLowerCase();

    if (epic.name?.toLowerCase().includes(term) || epic.epic_key?.toLowerCase().includes(term)) {
      return true;
    }

    const features = this.getFeatures(epic.epic_id);
    for (const feat of features) {
      if (this.isFeatureMatchingSearch(feat)) {
        return true;
      }
    }
    return false;
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  FEATURES
  // ══════════════════════════════════════════════════════════════════════════
  loadFeatures(epic: any) {
    const id = epic.epic_id;
    this.loadingFeatures.add(id);
    this.featureService
      .getAllFeatures({
        client_id: this.localStorage.getClientId(),
        epic_id: id,
        limit: 500,
      })
      .subscribe({
        next: (res: any) => {
          this.featuresMap.set(
            id,
            res?.rows || res?.data?.rows || res?.data || [],
          );
          this.loadingFeatures.delete(id);
        },
        error: () => {
          this.loadingFeatures.delete(id);
        },
      });
  }

  getFeatures(epicId: number): any[] {
    return this.featuresMap.get(epicId) || [];
  }
  isLoadingFeatures(epicId: number) {
    return this.loadingFeatures.has(epicId);
  }

  toggleFeature(epic: any, feat: any) {
    const id = feat.feature_id;
    if (this.expandedFeatures.has(id)) {
      this.expandedFeatures.delete(id);
    } else {
      this.expandedFeatures.add(id);
      if (!this.storiesMap.has(id)) this.loadUserStories(feat);
    }
  }

  isFeatureExpanded(featureId: number) {
    if (this.hierarchySearch && this.hierarchySearch.trim()) return true;
    return this.expandedFeatures.has(featureId);
  }

  isFeatureMatchingSearch(feat: any): boolean {
    if (!this.hierarchySearch || !this.hierarchySearch.trim()) return true;
    const term = this.hierarchySearch.trim().toLowerCase();

    if (feat.name?.toLowerCase().includes(term) || feat.feature_key?.toLowerCase().includes(term)) {
      return true;
    }

    const stories = this.getStories(feat.feature_id);
    for (const story of stories) {
      if (this.isStoryMatchingSearch(story)) {
        return true;
      }
    }
    return false;
  }

  isStoryMatchingSearch(story: any): boolean {
    if (!this.hierarchySearch || !this.hierarchySearch.trim()) return true;
    const term = this.hierarchySearch.trim().toLowerCase();

    if (story.title?.toLowerCase().includes(term) || story.story_key?.toLowerCase().includes(term)) {
      return true;
    }

    if (story.tasks && story.tasks.some((t: any) => (t.title || t.name)?.toLowerCase().includes(term))) {
      return true;
    }
    if (story.bugs && story.bugs.some((b: any) => (b.title || b.bug_title)?.toLowerCase().includes(term))) {
      return true;
    }

    return false;
  }

  getFilteredStoryTasks(): any[] {
    if (!this.storyTasks) return [];
    if (!this.hierarchySearch || !this.hierarchySearch.trim()) return this.storyTasks;
    const term = this.hierarchySearch.trim().toLowerCase();
    return this.storyTasks.filter((t: any) =>
      (t.title || t.name || t.task_key || '').toLowerCase().includes(term)
    );
  }

  getFilteredStoryBugs(): any[] {
    if (!this.storyBugs) return [];
    if (!this.hierarchySearch || !this.hierarchySearch.trim()) return this.storyBugs;
    const term = this.hierarchySearch.trim().toLowerCase();
    return this.storyBugs.filter((b: any) =>
      (b.title || b.bug_title || b.bug_code || '').toLowerCase().includes(term)
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  USER STORIES
  // ══════════════════════════════════════════════════════════════════════════
  loadUserStories(feat: any) {
    const id = feat.feature_id;
    this.loadingStories.add(id);
    this.userStoryService
      .getAllUserStories({
        client_id: this.localStorage.getClientId(),
        feature_id: id,
        limit: 500,
      })
      .subscribe({
        next: (res: any) => {
          this.storiesMap.set(
            id,
            res?.rows || res?.data?.rows || res?.data || [],
          );
          this.loadingStories.delete(id);
        },
        error: () => {
          this.loadingStories.delete(id);
        },
      });
  }

  getStories(featureId: number): any[] {
    return this.storiesMap.get(featureId) || [];
  }
  isLoadingStories(featureId: number) {
    return this.loadingStories.has(featureId);
  }

  selectStory(epic: any, feat: any, story: any) {
    this.selectedEpic = epic;
    this.selectedFeature = feat;
    this.selectedUserStory = story;
    this.selectedItem = null;
    this.storyTasks = [];
    this.rawStoryTasks = [];
    this.storyBugs = [];
    this.rawStoryBugs = [];
    this.backlogTaskSearch = '';
    this.backlogTaskStatusFilter = 'all';
    this.backlogBugSearch = '';
    this.backlogBugStatusFilter = 'all';
    this.displayedTasksLimit = 15;
    this.displayedBugsLimit = 15;
    this.storyTasksPage = 1;
    this.storyBugsPage = 1;
    this.loadStoryTasks(story, false);
    this.loadStoryBugs(story, false);
  }

  loadStoryTasks(story: any, append = false) {
    if (!story) return;
    this.loadingStoryTasks = true;
    if (!append) {
      this.storyTasksPage = 1;
    }

    const whereClause: any = {
      user_story_id: story.user_story_id,
      project_id: this.projectId,
    };
    if (this.backlogTaskStatusFilter && this.backlogTaskStatusFilter !== 'all') {
      whereClause.status = this.backlogTaskStatusFilter;
    }
    if (this.backlogOnlyMyTasks) {
      whereClause.only_my_tasks = true;
    }

    const payload: any = {
      user_id: this.localStorage.getUserId(),
      client_id: this.localStorage.getClientId(),
      page: this.storyTasksPage,
      limit: this.storyTasksPageSize,
      where: whereClause,
      order: { col: 'sort_order', dir: 'ASC' },
    };

    if (this.backlogTaskSearch?.trim()) {
      payload.search = this.backlogTaskSearch.trim();
    }

    this.taskService
      .getAllProjectTasks(payload)
      .subscribe({
        next: (res: any) => {
          const data = res?.data || res;
          const rows = data?.rows || data?.tasks || [];
          this.storyTasksTotal = data?.total ?? data?.count ?? data?.totalCount ?? rows.length;

          if (append) {
            this.rawStoryTasks = [...this.rawStoryTasks, ...rows];
          } else {
            this.rawStoryTasks = rows;
          }

          if (this.backlogOnlyMyTasks && this.currentUserId) {
            const uid = Number(this.currentUserId);
            this.storyTasks = (this.rawStoryTasks || []).filter((t: any) => {
              const isAssigned = Number(t.assignee_id) === uid ||
                (Array.isArray(t.assignee_ids) && t.assignee_ids.map(Number).includes(uid)) ||
                (t.assignees && t.assignees.some((a: any) => Number(a.user_id) === uid));
              const isCreated = Number(t.created_by) === uid || Number(t.reporter_id) === uid;
              return isAssigned || isCreated;
            });
          } else {
            this.storyTasks = [...this.rawStoryTasks];
          }

          this.loadingStoryTasks = false;
        },
        error: () => {
          this.loadingStoryTasks = false;
        },
      });
  }

  loadStoryBugs(story: any, append = false) {
    if (!story) return;
    this.loadingStoryBugs = true;
    if (!append) {
      this.storyBugsPage = 1;
    }

    const whereClause: any = {
      user_story_id: story.user_story_id,
      project_id: this.projectId,
    };
    if (this.backlogBugStatusFilter && this.backlogBugStatusFilter !== 'all') {
      whereClause.status = this.backlogBugStatusFilter;
    }
    if (this.backlogOnlyMyBugs) {
      whereClause.only_my_bugs = true;
    }

    const payload: any = {
      user_id: this.localStorage.getUserId(),
      client_id: this.localStorage.getClientId(),
      page: (this.storyBugsPage - 1) * this.storyBugsPageSize,
      limit: this.storyBugsPageSize,
      where: whereClause,
      order: { col: '"bug"."created_at"', dir: 'DESC' }, // Sort by newest bug at top
    };

    if (this.backlogBugSearch?.trim()) {
      payload.filter = this.backlogBugSearch.trim();
    }

    this.bugService
      .getAllBugs(payload)
      .subscribe({
        next: (res: any) => {
          const data = res?.data || res;
          const rows = data?.rows || data?.bugs || [];
          this.storyBugsTotal = data?.count ?? data?.total ?? data?.totalCount ?? rows.length;

          if (append) {
            this.rawStoryBugs = [...this.rawStoryBugs, ...rows];
          } else {
            this.rawStoryBugs = rows;
          }

          if (this.backlogOnlyMyBugs && this.currentUserId) {
            const uid = Number(this.currentUserId);
            this.storyBugs = (this.rawStoryBugs || []).filter((b: any) => {
              const isAssigned = Number(b.assigned_to) === uid ||
                Number(b.tester_id) === uid ||
                (Array.isArray(b.assigned_to_ids) && b.assigned_to_ids.map(Number).includes(uid));
              const isCreated = Number(b.created_by) === uid || Number(b.reported_by) === uid;
              return isAssigned || isCreated;
            });
          } else {
            this.storyBugs = [...this.rawStoryBugs];
          }

          this.loadingStoryBugs = false;
        },
        error: () => {
          this.loadingStoryBugs = false;
        },
      });
  }

  filterBacklogTasks() {
    this.storyTasksPage = 1;
    this.loadStoryTasks(this.selectedUserStory, false);
  }

  filterBacklogBugs() {
    this.storyBugsPage = 1;
    this.loadStoryBugs(this.selectedUserStory, false);
  }

  toggleBacklogMyTasks() {
    this.backlogOnlyMyTasks = !this.backlogOnlyMyTasks;
    this.filterBacklogTasks();
  }

  toggleBacklogMyBugs() {
    this.backlogOnlyMyBugs = !this.backlogOnlyMyBugs;
    this.filterBacklogBugs();
  }

  onLeftScroll(event: any) {
    const el = event.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
      this.loadMoreItems();
    }
  }

  onCenterScroll(event: any) {
    const el = event.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
      this.loadMoreItems();
    }
  }

  loadMoreItems() {
    // Left/center scroll load mores trigger page appends
    this.loadMoreTasks();
    this.loadMoreBugs();
  }

  loadMoreTasks() {
    if (this.storyTasks.length >= this.storyTasksTotal || this.loadingStoryTasks) return;
    this.storyTasksPage++;
    this.loadStoryTasks(this.selectedUserStory, true);
  }

  loadMoreBugs() {
    if (this.storyBugs.length >= this.storyBugsTotal || this.loadingStoryBugs) return;
    this.storyBugsPage++;
    this.loadStoryBugs(this.selectedUserStory, true);
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  RIGHT PANEL
  // ══════════════════════════════════════════════════════════════════════════
  selectTask(task: any) {
    if (task?._itemType === 'bug' || task?.bug_id) {
      this.selectBug(task);
      return;
    }
    if (
      this.selectedItem?._type === 'task' &&
      this.selectedItem?.task_id === task.task_id
    ) {
      this.closeRightPanel();
      return;
    }
    this.selectedItem = { ...task, _type: 'task' };
    this.rightPanelTab = 'comments';
    this.loadTaskDetails(task.task_id);
  }

  loadTaskDetails(taskId: any) {
    this.loadingRightPanel = true;
    this.taskService
      .getProjectTaskById({
        where: {
          client_id: this.localStorage.getClientId(),
          project_id: this.projectId,
          task_id: taskId,
        },
      })
      .subscribe({
        next: (res: any) => {
          if (res) this.selectedItem = { ...res, _type: 'task' };
          this.loadingRightPanel = false;
        },
        error: () => {
          this.loadingRightPanel = false;
        },
      });
  }

  selectBug(bug: any) {
    if (
      this.selectedItem?._type === 'bug' &&
      this.selectedItem?.bug_id === bug.bug_id
    ) {
      this.closeRightPanel();
      return;
    }
    this.selectedItem = { ...bug, _type: 'bug' };
    this.rightPanelTab = 'details';
  }

  closeRightPanel() {
    this.selectedItem = null;
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  TASK FORM MODAL
  // ══════════════════════════════════════════════════════════════════════════
  openAddTask(content: any, sprintId?: number | string | null) {
    this.taskFormAction = 'Add';
    this.currentTaskId = null;
    this.activeTaskTab = 1;
    this.selectedFormEpicId = this.selectedEpic?.epic_id || null;
    this.selectedFormFeatureId = this.selectedFeature?.feature_id || null;
    this.formFeatures = this.selectedEpic
      ? this.getFeatures(this.selectedEpic.epic_id)
      : [];
    this.formUserStories = this.selectedFeature
      ? this.getStories(this.selectedFeature.feature_id)
      : [];
    const resolvedSprintId =
      sprintId ?? this.selectedSprint?.sprint_id ?? this.activeSprint?.sprint_id ?? null;
    const today = new Date().toISOString().substring(0, 10);
    const nextWeek = new Date(Date.now() + 7 * 86400000).toISOString().substring(0, 10);
    this.taskData = {
      project_id: this.projectId,
      client_id: this.localStorage.getClientId(),
      title: '',
      description: '',
      priority: 'medium',
      status: 'todo',
      task_type: 'task',
      progress_percentage: 0,
      assignee_id: null,
      assignee_ids: [],
      user_story_id: this.selectedUserStory?.user_story_id || null,
      estimated_hours: null,
      start_date: today,
      due_date: nextWeek,
      sprint_id: resolvedSprintId,
    };
    if (this.selectedUserStory?.sprint_id != null) {
      this.taskData.sprint_id = Number(this.selectedUserStory.sprint_id);
    }
    this.selectedTaskAssigneeObjects = [];
    this.loadProjectMembers();
    this.loadDepartments();
    this.loadModules();
    this.loadProjectBugs();
    this.modalService.open(content, {
      size: 'xl',
      centered: true,
      backdrop: 'static',
      scrollable: true,
    });
  }

  openEditTask(content: any, task: any) {
    if (task?._itemType === 'bug' || task?.bug_id) {
      this.openEditBug(this.bugModal, task);
      return;
    }
    this.taskFormAction = 'Update';
    this.currentTaskId = task.task_id;
    this.activeTaskTab = 1;
    this.taskData = { ...task };
    const initTaskSprintId = task.sprint_id ?? task.user_story?.sprint_id ?? null;
    this.taskData.sprint_id = initTaskSprintId != null ? Number(initTaskSprintId) : null;
    const initTaskModuleId = task.module_id ?? task.module?.module_id ?? task.project_module?.module_id ?? null;
    this.taskData.module_id = initTaskModuleId != null ? Number(initTaskModuleId) : null;
    this.taskData.assignee_ids =
      this.taskData.assignee_ids ||
      (this.taskData.assignee_id ? [this.taskData.assignee_id] : []);
    this.selectedTaskAssigneeObjects = (this.taskData.assignee_ids || []).map(
      (uid: any) => {
        const matched = this.projectMembers.find(
          (m) => Number(m.user_id) === Number(uid),
        );
        return {
          item_id: uid,
          item_text: matched
            ? `${matched.prefix || ''} ${matched.fname || ''} ${matched.lname || ''}`
                .replace(/\s+/g, ' ')
                .trim()
            : `User ${uid}`,
        };
      },
    );
    this.taskData.attachments = this.taskData.attachments || [];
    if (this.taskData.due_date)
      this.taskData.due_date = new Date(this.taskData.due_date)
        .toISOString()
        .substring(0, 10);
    if (this.taskData.start_date)
      this.taskData.start_date = new Date(this.taskData.start_date)
        .toISOString()
        .substring(0, 10);
    this.selectedFormEpicId = null;
    this.selectedFormFeatureId = null;
    this.formFeatures = [];
    this.formUserStories = [];
    this.loadProjectMembers();
    this.loadDepartments();
    this.loadModules();
    this.modalService.open(content, {
      size: 'xl',
      centered: true,
      backdrop: 'static',
      scrollable: true,
    });

    this.detailsLoading = true;
    this.taskService
      .getProjectTaskById({
        where: {
          client_id: this.localStorage.getClientId(),
          project_id: this.projectId,
          task_id: this.currentTaskId,
        },
      })
      .subscribe({
        next: (res: any) => {
          this.detailsLoading = false;
          if (!res) return;
          this.taskData = { ...this.taskData, ...res };
          let epicId = res?.user_story?.feature?.epic?.epic_id || null;
          let featureId = res?.user_story?.feature?.feature_id || null;
          let storyId =
            res?.user_story?.user_story_id ||
            res?.user_story_id ||
            this.taskData.user_story_id ||
            null;

          const matchedStory =
            (this.projectStories || []).find(
              (st: any) => Number(st.user_story_id) === Number(storyId),
            ) ||
            (this.formUserStories || []).find(
              (st: any) => Number(st.user_story_id) === Number(storyId),
            );

          const taskSprintId =
            res.sprint_id ??
            res.user_story?.sprint_id ??
            matchedStory?.sprint_id ??
            this.taskData.sprint_id ??
            null;
          this.taskData.sprint_id =
            taskSprintId != null ? Number(taskSprintId) : null;

          const taskModuleId =
            res.module_id ??
            res.module?.module_id ??
            res.project_module?.module_id ??
            this.taskData.module_id ??
            null;
          this.taskData.module_id =
            taskModuleId != null ? Number(taskModuleId) : null;

          this.taskData.assignee_ids =
            this.taskData.assignee_ids ||
            (this.taskData.assignee_id ? [this.taskData.assignee_id] : []);
          this.selectedTaskAssigneeObjects = (
            this.taskData.assignee_ids || []
          ).map((uid: any) => {
            const matched = this.projectMembers.find(
              (m) => Number(m.user_id) === Number(uid),
            );
            return {
              item_id: uid,
              item_text: matched
                ? `${matched.prefix || ''} ${matched.fname || ''} ${matched.lname || ''}`
                    .replace(/\s+/g, ' ')
                    .trim()
                : `User ${uid}`,
            };
          });
          this.taskData.attachments = this.taskData.attachments || [];
          if (storyId && (!epicId || !featureId)) {
            for (const ep of this.epics || []) {
              for (const ft of ep.features || []) {
                const foundStory = (ft.user_stories || ft.stories || []).find(
                  (st: any) => Number(st.user_story_id) === Number(storyId),
                );
                if (foundStory) {
                  epicId = ep.epic_id;
                  featureId = ft.feature_id;
                  break;
                }
              }
              if (epicId) break;
            }
          }

          this.selectedFormEpicId = epicId != null ? Number(epicId) : null;
          this.selectedFormFeatureId =
            featureId != null ? Number(featureId) : null;
          this.taskData.user_story_id =
            storyId != null ? Number(storyId) : null;

          if (this.selectedFormEpicId) {
            this.loadFormFeatures(this.selectedFormEpicId, () => {
              if (this.selectedFormFeatureId) {
                this.loadFormUserStories(this.selectedFormFeatureId);
              }
            });
          }
          if (this.taskData.due_date)
            this.taskData.due_date = new Date(this.taskData.due_date)
              .toISOString()
              .substring(0, 10);
          if (this.taskData.start_date)
            this.taskData.start_date = new Date(this.taskData.start_date)
              .toISOString()
              .substring(0, 10);
        },
        error: () => {
          this.detailsLoading = false;
        },
      });
  }

  submitTask() {
    if (!this.taskData.title?.trim()) {
      this.toastr.error('Task title is required');
      return;
    }
    this.btnLoading = true;
    this.taskData.assignee_ids = (this.selectedTaskAssigneeObjects || []).map(
      (item) => Number(item.item_id),
    );
    const payload = {
      ...this.taskData,
      updated_by: this.localStorage.getUserId(),
      created_by: this.localStorage.getUserId(),
      reporter_id: this.localStorage.getUserId(),
    };
    if (!payload.bug_id) delete payload.bug_id;
    if (!payload.module_id) delete payload.module_id;
    if (!payload.department_id) delete payload.department_id;
    if (!payload.assignee_id) delete payload.assignee_id;

    const isUpdate = this.taskFormAction === 'Update';
    if (isUpdate) payload.where = { task_id: this.currentTaskId };

    const req = isUpdate
      ? this.taskService.updateProjectTask(payload)
      : this.taskService.createProjectTask(payload);
    req.subscribe({
      next: (data: any) => {
        this.btnLoading = false;
        this.toastr.success(
          `Task ${isUpdate ? 'updated' : 'created'} successfully`,
        );
        if (!isUpdate && data?.data?.task_id) {
          this.taskFormAction = 'Update';
          this.currentTaskId = data.data.task_id;
        }
        if (this.selectedUserStory) this.loadStoryTasks(this.selectedUserStory);
        this.loadAllProjectTasks();
        this.loadProjectBugs();
        if (this.tasksChild) this.tasksChild.loadTasks();
        if (this.bugsChild) this.bugsChild.loadBugs();
        if (this.kanbanChild) this.kanbanChild.loadTasks();
        if (this.sprintChild) {
          this.sprintChild.loadSprintTasks();
          this.sprintChild.loadSprintBugs();
        }
        if (this.selectedEpic) this.loadEpics();
        this.modalService.dismissAll();
      },
      error: (e: any) => {
        this.btnLoading = false;
        this.toastr.error(e?.error?.message || 'Failed');
      },
    });
  }

  deleteTask(task: any, event: Event) {
    if (task?._itemType === 'bug' || task?.bug_id) {
      this.deleteBug(task, event);
      return;
    }
    event.stopPropagation();
    if (!confirm(`Delete task: ${task.title}?`)) return;
    this.taskService
      .deleteProjectTask({
        where: { task_id: task.task_id },
        deleted_by: this.localStorage.getUserId(),
      })
      .subscribe({
        next: () => {
          this.toastr.success('Task deleted');
          if (this.selectedUserStory)
            this.loadStoryTasks(this.selectedUserStory);
          this.loadAllProjectTasks();
          this.loadProjectBugs();
          if (this.tasksChild) this.tasksChild.loadTasks();
          if (this.bugsChild) this.bugsChild.loadBugs();
          if (this.kanbanChild) this.kanbanChild.loadTasks();
          if (this.sprintChild) {
            this.sprintChild.loadSprintTasks();
            this.sprintChild.loadSprintBugs();
          }
          if (this.selectedItem?.task_id === task.task_id)
            this.closeRightPanel();
        },
        error: () => this.toastr.error('Failed to delete task'),
      });
  }

  onTaskStatusChange(status: string) {
    if (status === 'todo') this.taskData.progress_percentage = 0;
    else if (status === 'in_progress') this.taskData.progress_percentage = 50;
    else if (status === 'in_review') this.taskData.progress_percentage = 90;
    else if (status === 'completed') this.taskData.progress_percentage = 100;
  }

  onFormEpicChange(epicId: any) {
    this.selectedFormEpicId = epicId;
    this.selectedFormFeatureId = null;
    this.formFeatures = [];
    this.formUserStories = [];
    this.taskData.user_story_id = null;
    this.bugData.user_story_id = null;
    if (epicId) this.loadFormFeatures(epicId, () => {});
  }

  loadFormFeatures(epicId: any, callback: () => void) {
    this.featureService
      .getAllFeatures({
        client_id: this.localStorage.getClientId(),
        epic_id: epicId,
        limit: 500,
      })
      .subscribe({
        next: (res: any) => {
          const raw = res?.rows || res?.data?.rows || res?.data || [];
          this.formFeatures = (Array.isArray(raw) ? raw : []).map((ft: any) => ({
            ...ft,
            feature_id: Number(ft.feature_id),
          }));
          callback();
        },
        error: () => {},
      });
  }

  onFormFeatureChange(featureId: any) {
    this.selectedFormFeatureId = featureId;
    this.formUserStories = [];
    this.taskData.user_story_id = null;
    this.bugData.user_story_id = null;
    if (featureId) this.loadFormUserStories(featureId);
  }

  onTaskUserStoryChange(storyId: any) {
    if (!storyId) return;
    const story =
      (this.formUserStories || []).find(
        (s) => Number(s.user_story_id) === Number(storyId),
      ) ||
      (this.projectStories || []).find(
        (s) => Number(s.user_story_id) === Number(storyId),
      );
    if (story?.sprint_id != null) {
      this.taskData.sprint_id = Number(story.sprint_id);
    }
  }

  onBugUserStoryChange(storyId: any) {
    if (!storyId) return;
    const story =
      (this.formUserStories || []).find(
        (s) => Number(s.user_story_id) === Number(storyId),
      ) ||
      (this.projectStories || []).find(
        (s) => Number(s.user_story_id) === Number(storyId),
      );
    if (story?.sprint_id != null) {
      this.bugData.sprint_id = Number(story.sprint_id);
    }
  }

  loadFormUserStories(featureId: any) {
    this.userStoryService
      .getAllUserStories({
        client_id: this.localStorage.getClientId(),
        feature_id: featureId,
        limit: 500,
      })
      .subscribe({
        next: (res: any) => {
          const raw = res?.rows || res?.data?.rows || res?.data || [];
          this.formUserStories = (Array.isArray(raw) ? raw : []).map((st: any) => ({
            ...st,
            user_story_id: Number(st.user_story_id),
          }));
        },
        error: () => {},
      });
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  BUG FORM MODAL
  // ══════════════════════════════════════════════════════════════════════════
  openAddBug(content: any) {
    this.bugFormAction = 'Add';
    this.currentBugId = null;
    this.activeBugTab = 1;
    this.selectedFormEpicId = this.selectedEpic?.epic_id || null;
    this.selectedFormFeatureId = this.selectedFeature?.feature_id || null;
    this.formFeatures = this.selectedEpic
      ? this.getFeatures(this.selectedEpic.epic_id)
      : [];
    this.formUserStories = this.selectedFeature
      ? this.getStories(this.selectedFeature.feature_id)
      : [];
    this.bugData = {
      project_id: this.projectId,
      client_id: this.localStorage.getClientId(),
      created_by: this.localStorage.getUserId(),
      title: '',
      description: '',
      steps_to_reproduce: '',
      type: '',
      priority: 'medium',
      status: 'open',
      severity: 'medium',
      environment: '',
      assigned_to: null,
      assigned_to_ids: [],
      tester_id: null,
      reporter_id: this.localStorage.getUserId(),
      module_id: null,
      device_info: '',
      os_info: '',
      browser_info: '',
      app_version: '',
      internal_comments: '',
      user_story_id: this.selectedUserStory?.user_story_id || null,
      due_date: new Date(Date.now() + 7 * 86400000).toISOString().substring(0, 10),
      attachments: [],
      sprint_id: this.selectedUserStory?.sprint_id
        ? Number(this.selectedUserStory.sprint_id)
        : this.activeSprint?.sprint_id
          ? Number(this.activeSprint.sprint_id)
          : null,
    };
    this.selectedBugAssigneeObjects = [];
    this.loadProjectMembers();
    this.loadModules();
    this.modalService.open(content, {
      size: 'xl',
      centered: true,
      backdrop: 'static',
      scrollable: true,
    });
  }

  openEditBug(content: any, bug: any) {
    this.bugFormAction = 'Update';
    this.currentBugId = bug.bug_id;
    this.activeBugTab = 1;
    this.selectedFormEpicId = null;
    this.selectedFormFeatureId = null;
    this.formFeatures = [];
    this.formUserStories = [];
    // Seed with list-row data immediately so form isn't blank while loading
    this.bugData = { ...bug };
    const initBugSprintId = bug.sprint_id ?? bug.user_story?.sprint_id ?? null;
    this.bugData.sprint_id = initBugSprintId != null ? Number(initBugSprintId) : null;
    const initBugModuleId = bug.module_id ?? bug.project_module?.module_id ?? bug.module?.module_id ?? null;
    this.bugData.module_id = initBugModuleId != null ? Number(initBugModuleId) : null;
    this.bugData.assigned_to_ids =
      this.bugData.assigned_to_ids ||
      (this.bugData.assigned_to ? [this.bugData.assigned_to] : []);
    this.selectedBugAssigneeObjects = (this.bugData.assigned_to_ids || []).map(
      (uid: any) => {
        const matched = this.projectMembers.find(
          (m) => Number(m.user_id) === Number(uid),
        );
        return {
          item_id: uid,
          item_text: matched
            ? `${matched.prefix || ''} ${matched.fname || ''} ${matched.lname || ''}`
                .replace(/\s+/g, ' ')
                .trim()
            : `User ${uid}`,
        };
      },
    );
    this.bugData.steps_to_reproduce = this.bugData.steps_to_reproduce || '';
    this.bugData.internal_comments = this.bugData.internal_comments || '';
    this.bugData.attachments = this.bugData.attachments || [];
    if (this.bugData.due_date)
      this.bugData.due_date = new Date(this.bugData.due_date)
        .toISOString()
        .substring(0, 10);

    this.loadProjectMembers();
    this.loadModules();
    this.modalService.open(content, {
      size: 'xl',
      centered: true,
      backdrop: 'static',
      scrollable: true,
    });

    // Fetch full bug details (includes module, tester, user_story.feature.epic)
    this.bugDetailsLoading = true;
    this.bugService
      .getBugById({
        bug_id: bug.bug_id,
        client_id: this.localStorage.getClientId(),
        project_id: this.projectId,
      })
      .subscribe({
        next: (res: any) => {
          this.bugDetailsLoading = false;
          if (!res) return;
          // Merge full data into bugData
          this.bugData = { ...this.bugData, ...res };
          let epicId = res?.user_story?.feature?.epic?.epic_id || null;
          let featureId = res?.user_story?.feature?.feature_id || null;
          let storyId =
            res?.user_story?.user_story_id ||
            res?.user_story_id ||
            this.bugData.user_story_id ||
            null;

          const matchedStory =
            (this.projectStories || []).find(
              (st: any) => Number(st.user_story_id) === Number(storyId),
            ) ||
            (this.formUserStories || []).find(
              (st: any) => Number(st.user_story_id) === Number(storyId),
            );

          const bugSprintId =
            res.sprint_id ??
            res.user_story?.sprint_id ??
            matchedStory?.sprint_id ??
            this.bugData.sprint_id ??
            null;
          this.bugData.sprint_id =
            bugSprintId != null ? Number(bugSprintId) : null;

          const bugModuleId =
            res.module_id ??
            res.project_module?.module_id ??
            res.module?.module_id ??
            this.bugData.module_id ??
            null;
          this.bugData.module_id =
            bugModuleId != null ? Number(bugModuleId) : null;
          this.bugData.assigned_to_ids =
            this.bugData.assigned_to_ids ||
            (this.bugData.assigned_to ? [this.bugData.assigned_to] : []);
          this.selectedBugAssigneeObjects = (
            this.bugData.assigned_to_ids || []
          ).map((uid: any) => {
            const matched = this.projectMembers.find(
              (m) => Number(m.user_id) === Number(uid),
            );
            return {
              item_id: uid,
              item_text: matched
                ? `${matched.prefix || ''} ${matched.fname || ''} ${matched.lname || ''}`
                    .replace(/\s+/g, ' ')
                    .trim()
                : `User ${uid}`,
            };
          });
          this.bugData.steps_to_reproduce = this.bugData.steps_to_reproduce || '';
          this.bugData.internal_comments = this.bugData.internal_comments || '';
          this.bugData.attachments = this.bugData.attachments || [];
          if (this.bugData.due_date)
            this.bugData.due_date = new Date(this.bugData.due_date)
              .toISOString()
              .substring(0, 10);
          
          if (storyId && (!epicId || !featureId)) {
            for (const ep of this.epics || []) {
              for (const ft of ep.features || []) {
                const foundStory = (ft.user_stories || ft.stories || []).find(
                  (st: any) => Number(st.user_story_id) === Number(storyId),
                );
                if (foundStory) {
                  epicId = ep.epic_id;
                  featureId = ft.feature_id;
                  break;
                }
              }
              if (epicId) break;
            }
          }

          this.selectedFormEpicId = epicId != null ? Number(epicId) : null;
          this.selectedFormFeatureId =
            featureId != null ? Number(featureId) : null;
          this.bugData.user_story_id =
            storyId != null ? Number(storyId) : null;

          if (this.selectedFormEpicId) {
            this.loadFormFeatures(this.selectedFormEpicId, () => {
              if (this.selectedFormFeatureId) {
                this.loadFormUserStories(this.selectedFormFeatureId);
              }
            });
          }
        },
        error: () => {
          this.bugDetailsLoading = false;
        },
      });
  }

  submitBug() {
    if (!this.bugData.title?.trim()) {
      this.toastr.error('Bug title is required');
      return;
    }
    this.btnLoading = true;
    this.bugData.assigned_to_ids = (this.selectedBugAssigneeObjects || []).map(
      (item) => Number(item.item_id),
    );
    const isUpdate = this.bugFormAction === 'Update';
    const payload = {
      ...this.bugData,
      updated_by: this.localStorage.getUserId(),
      created_by: this.bugData.created_by || this.localStorage.getUserId(),
      reporter_id: this.bugData.reporter_id || this.localStorage.getUserId(),
    };
    if (!payload.assigned_to) delete payload.assigned_to;
    if (!payload.tester_id) delete payload.tester_id;
    if (!payload.module_id) delete payload.module_id;
    if (isUpdate) {
      payload.bug_id = this.currentBugId;
      payload.where = { bug_id: this.currentBugId };
    }

    const req = isUpdate
      ? this.bugService.updateBug(payload)
      : this.bugService.createBug(payload);
    req.subscribe({
      next: () => {
        this.btnLoading = false;
        this.toastr.success(
          `Bug ${isUpdate ? 'updated' : 'created'} successfully`,
        );
        if (this.selectedUserStory) this.loadStoryBugs(this.selectedUserStory);
        this.loadAllProjectTasks();
        this.loadProjectBugs();
        if (this.bugsChild) this.bugsChild.loadBugs();
        if (this.tasksChild) this.tasksChild.loadTasks();
        if (this.kanbanChild) this.kanbanChild.loadTasks();
        if (this.sprintChild) {
          this.sprintChild.loadSprintTasks();
          this.sprintChild.loadSprintBugs();
        }
        if (this.selectedEpic) this.loadEpics();
        this.modalService.dismissAll();
      },
      error: (e: any) => {
        this.btnLoading = false;
        this.toastr.error(e?.error?.message || 'Failed');
      },
    });
  }

  deleteBug(bug: any, event: Event) {
    event.stopPropagation();
    if (!confirm(`Delete bug: ${bug.title}?`)) return;
    this.bugService
      .deleteBug({
        where: { bug_id: bug.bug_id },
        deleted_by: this.localStorage.getUserId(),
      })
      .subscribe({
        next: () => {
          this.toastr.success('Bug deleted');
          if (this.selectedUserStory)
            this.loadStoryBugs(this.selectedUserStory);
          this.loadAllProjectTasks();
          this.loadProjectBugs();
          if (this.bugsChild) this.bugsChild.loadBugs();
          if (this.tasksChild) this.tasksChild.loadTasks();
          if (this.kanbanChild) this.kanbanChild.loadTasks();
          if (this.sprintChild) {
            this.sprintChild.loadSprintTasks();
            this.sprintChild.loadSprintBugs();
          }
          if (this.selectedItem?.bug_id === bug.bug_id) this.closeRightPanel();
        },
        error: () => this.toastr.error('Failed to delete bug'),
      });
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  EPIC / FEATURE / STORY MODALS
  // ══════════════════════════════════════════════════════════════════════════
  openEpicModal(content: any, epic?: any) {
    this.formAction = epic ? 'Update' : 'Add';
    this.epicData = epic
      ? { ...epic }
      : {
          project_id: this.projectId,
          client_id: this.localStorage.getClientId(),
          name: '',
          description: '',
          status: 'todo',
          priority: 'medium',
        };
    this.modalService.open(content, { centered: true, size: 'md' });
  }

  submitEpic() {
    this.btnLoading = true;
    const req =
      this.formAction === 'Add'
        ? this.epicService.createEpic(this.epicData)
        : this.epicService.updateEpic(this.epicData);
    req.subscribe({
      next: () => {
        this.toastr.success(
          `Epic ${this.formAction === 'Add' ? 'created' : 'updated'}`,
        );
        this.modalService.dismissAll();
        this.loadEpics();
        this.btnLoading = false;
      },
      error: (e: any) => {
        this.toastr.error(e?.error?.message || 'Failed');
        this.btnLoading = false;
      },
    });
  }

  deleteEpic(epic: any, event: Event) {
    event.stopPropagation();
    if (!confirm(`Delete Epic: ${epic.name}?`)) return;
    this.epicService
      .deleteEpic({
        client_id: this.localStorage.getClientId(),
        epic_id: epic.epic_id,
      })
      .subscribe({
        next: () => {
          this.toastr.success('Epic deleted');
          this.loadEpics();
        },
        error: () => this.toastr.error('Failed to delete epic'),
      });
  }

  openFeatureModal(content: any, epic: any, feat?: any) {
    this.formAction = feat ? 'Update' : 'Add';
    this.featureData = feat
      ? { ...feat }
      : {
          epic_id: epic.epic_id,
          client_id: this.localStorage.getClientId(),
          name: '',
          description: '',
          status: 'todo',
          priority: 'medium',
        };
    this.modalService.open(content, { centered: true, size: 'md' });
  }

  submitFeature() {
    this.btnLoading = true;
    const req =
      this.formAction === 'Add'
        ? this.featureService.createFeature(this.featureData)
        : this.featureService.updateFeature(this.featureData);
    req.subscribe({
      next: () => {
        this.toastr.success(
          `Feature ${this.formAction === 'Add' ? 'created' : 'updated'}`,
        );
        const epicId = this.featureData.epic_id;
        this.featuresMap.delete(epicId);
        this.loadFeatures({ epic_id: epicId });
        this.modalService.dismissAll();
        this.btnLoading = false;
      },
      error: (e: any) => {
        this.toastr.error(e?.error?.message || 'Failed');
        this.btnLoading = false;
      },
    });
  }

  deleteFeature(feat: any, epic: any, event: Event) {
    event.stopPropagation();
    if (!confirm(`Delete Feature: ${feat.name}?`)) return;
    this.featureService
      .deleteFeature({
        client_id: this.localStorage.getClientId(),
        feature_id: feat.feature_id,
      })
      .subscribe({
        next: () => {
          this.toastr.success('Feature deleted');
          this.featuresMap.delete(epic.epic_id);
          this.loadFeatures(epic);
        },
        error: () => this.toastr.error('Failed to delete feature'),
      });
  }

  openUserStoryModal(content: any, feat: any, story?: any) {
    this.formAction = story ? 'Update' : 'Add';
    this.userStoryData = story
      ? { ...story }
      : {
          feature_id: feat.feature_id,
          client_id: this.localStorage.getClientId(),
          title: '',
          description: '',
          status: 'todo',
          priority: 'medium',
          story_points: 0,
          sprint_id: null,
        };
    this.modalService.open(content, { centered: true, size: 'md' });
  }

  openEditStoryFromSprint(story: any) {
    this.formAction = 'Update';
    this.userStoryData = { ...story, client_id: this.localStorage.getClientId() };
    this.modalService.open(this.storyModal, { centered: true, size: 'md' });
  }

  submitUserStory() {
    this.btnLoading = true;
    const req =
      this.formAction === 'Add'
        ? this.userStoryService.createUserStory(this.userStoryData)
        : this.userStoryService.updateUserStory(this.userStoryData);
    req.subscribe({
      next: () => {
        this.toastr.success(
          `User Story ${this.formAction === 'Add' ? 'created' : 'updated'}`,
        );
        const featId = this.userStoryData.feature_id;
        this.storiesMap.delete(featId);
        this.loadUserStories({ feature_id: featId });
        this.modalService.dismissAll();
        this.btnLoading = false;
      },
      error: (e: any) => {
        this.toastr.error(e?.error?.message || 'Failed');
        this.btnLoading = false;
      },
    });
  }

  deleteUserStory(story: any, feat: any, event: Event) {
    event.stopPropagation();
    if (!confirm(`Delete Story: ${story.title}?`)) return;
    this.userStoryService
      .deleteUserStory({
        client_id: this.localStorage.getClientId(),
        user_story_id: story.user_story_id,
      })
      .subscribe({
        next: () => {
          this.toastr.success('User Story deleted');
          this.storiesMap.delete(feat.feature_id);
          this.loadUserStories(feat);
          if (this.selectedUserStory?.user_story_id === story.user_story_id) {
            this.selectedUserStory = null;
            this.selectedItem = null;
          }
        },
        error: () => this.toastr.error('Failed to delete story'),
      });
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  SPRINT MANAGEMENT
  // ══════════════════════════════════════════════════════════════════════════
  loadSprints() {
    this.loadingSprints = true;
    this.sprintService
      .getAllSprints({
        project_id: this.projectId,
        client_id: this.localStorage.getClientId(),
        limit: 500,
      })
      .subscribe({
        next: (res: any) => {
          const raw = res?.rows || res?.data?.rows || res?.data || res || [];
          this.sprints = (Array.isArray(raw) ? raw : []).map((s: any) => ({
            ...s,
            sprint_id: Number(s.sprint_id),
          }));
          this.loadingSprints = false;
          if (this.sprints.length > 0) {
            if (this.selectedSprint) {
              const updated = this.sprints.find(
                (s) =>
                  Number(s.sprint_id) === Number(this.selectedSprint.sprint_id),
              );
              if (updated) this.selectedSprint = updated;
            } else {
              this.selectedSprint =
                this.sprints.find((s) => s.status === 'active') ||
                this.sprints[0];
            }
          }
          if (this.kanbanChild) {
            this.kanbanChild.loadTasks();
          }
        },
        error: () => {
          this.loadingSprints = false;
        },
      });
  }

  loadAllProjectStories() {
    this.loadingProjectStories = true;
    this.userStoryService
      .getAllUserStories({
        project_id: this.projectId,
        client_id: this.localStorage.getClientId(),
        limit: 1000,
      })
      .subscribe({
        next: (res: any) => {
          this.projectStories =
            res?.rows || res?.data?.rows || res?.data || res || [];
          this.loadingProjectStories = false;
        },
        error: () => {
          this.loadingProjectStories = false;
        },
      });
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  HELPERS
  // ══════════════════════════════════════════════════════════════════════════
  getStatusClass(status: string): string {
    const map: any = {
      open: 'st-open',
      in_progress: 'st-in-progress',
      closed: 'st-closed',
      todo: 'st-todo',
      completed: 'st-completed',
      're-open': 'st-re-open',
      in_review: 'st-review',
      ready_for_test: 'st-review',
      rejected: 'st-closed',
      cancelled: 'st-closed',
      blocked: 'st-blocked',
      not_started: 'st-todo',
    };
    return map[status] || 'st-todo';
  }

  getPriorityClass(priority: string): string {
    const map: any = {
      low: 'pri-low',
      medium: 'pri-medium',
      high: 'pri-high',
      urgent: 'pri-urgent',
      critical: 'pri-urgent',
    };
    return map[priority] || 'pri-medium';
  }

  getSeverityClass(severity: string): string {
    const map: any = {
      low: 'sev-low',
      medium: 'sev-medium',
      high: 'sev-high',
      critical: 'sev-critical',
      blocker: 'sev-critical',
    };
    return map[severity] || 'sev-medium';
  }

  prettyLabel(s: string) {
    return (s || '')
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  getSprintName(sprintId: any): string {
    const sprint = (this.sprints || []).find(
      (s) => Number(s.sprint_id) === Number(sprintId),
    );
    return sprint ? sprint.name : `Sprint ${sprintId}`;
  }

  getInitials(user: any): string {
    if (!user) return '?';
    const f = (user.fname || user.prefix || '')[0] || '';
    const l = (user.lname || '')[0] || '';
    return (f + l).toUpperCase() || '?';
  }

  getDaysRemaining(endDate: string): number {
    if (!endDate) return 0;
    const diff = new Date(endDate).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }

  get activeSprint(): any {
    return (
      (this.sprints || []).find((s) => s.status?.toLowerCase() === 'active') ||
      null
    );
  }

  getSprintProgressInfo(sprint: any): {
    completed: number;
    total: number;
    pct: number;
    completedTasks: number;
    totalTasks: number;
    closedBugs: number;
    totalBugs: number;
  } {
    if (!sprint) return { completed: 0, total: 0, pct: 0, completedTasks: 0, totalTasks: 0, closedBugs: 0, totalBugs: 0 };
    const sid = Number(sprint.sprint_id);

    // 1. Get all story IDs belonging to this sprint
    const stories = (this.projectStories || []).filter(
      (s: any) => Number(s.sprint_id) === sid
    );
    const storyIdsInSprint = new Set<number>(
      stories.map((s: any) => Number(s.user_story_id)).filter(Boolean)
    );

    // 2. Filter Tasks in Sprint (direct sprint_id OR via story in sprint)
    const sprintTasks = (this.allProjectTasks || []).filter((t: any) => {
      if (Number(t.sprint_id) === sid) return true;
      if (t.user_story_id && storyIdsInSprint.has(Number(t.user_story_id))) return true;
      return false;
    });

    const completedTasks = sprintTasks.filter((t: any) => {
      const s = this.normalizeStatusKey(t.status || t.project_task_status);
      return s === 'completed' || s === 'done' || s === 'closed' || s === 'resolved';
    }).length;

    // 3. Filter Bugs in Sprint (direct sprint_id OR via story in sprint)
    const sprintBugs = (this.allProjectBugs || []).filter((b: any) => {
      if (Number(b.sprint_id) === sid) return true;
      if (b.user_story_id && storyIdsInSprint.has(Number(b.user_story_id))) return true;
      return false;
    });

    const closedBugs = sprintBugs.filter((b: any) => {
      const s = this.normalizeStatusKey(b.status);
      return s === 'closed' || s === 'resolved' || s === 'rejected' || s === 'completed';
    }).length;

    // Total tasks + bugs in sprint
    let total = sprintTasks.length + sprintBugs.length;
    let completed = completedTasks + closedBugs;

    // Fallback if no tasks/bugs in sprint, check story completion
    if (total === 0 && stories.length > 0) {
      total = stories.length;
      completed = stories.filter((s: any) => {
        const st = this.normalizeStatusKey(s.status);
        return st === 'completed' || st === 'closed';
      }).length;
    }

    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

    return {
      completed,
      total,
      pct,
      completedTasks,
      totalTasks: sprintTasks.length,
      closedBugs,
      totalBugs: sprintBugs.length
    };
  }

  getSprintStoryPoints(sprint: any): {
    completed: number;
    total: number;
    pct: number;
  } {
    const info = this.getSprintProgressInfo(sprint);
    return {
      completed: info.completed,
      total: info.total,
      pct: info.pct
    };
  }

  getSprintDurationDays(sprint: any): number {
    if (!sprint || !sprint.start_date || !sprint.end_date) return 0;
    const start = new Date(sprint.start_date).getTime();
    const end = new Date(sprint.end_date).getTime();
    const diff = end - start;
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }

  getAvatarColor(user: any): string {
    const colors = [
      '#5b6af9',
      '#10b981',
      '#f59e0b',
      '#ef4444',
      '#8b5cf6',
      '#06b6d4',
      '#ec4899',
    ];
    const name = (user?.fname || '') + (user?.lname || '');
    let hash = 0;
    for (let i = 0; i < name.length; i++)
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  }

  isImage(item: any): boolean {
    if (!item) return false;
    const imageTypes = [
      'image/jpeg',
      'image/png',
      'image/jpg',
      'image/webp',
      'image/gif',
    ];
    if (item.type) {
      return imageTypes.includes(item.type);
    }
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(item.url);
  }

  onImgError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/img/file-placeholder.png';
  }

  openPreview(attachments: any[], i: number) {
    this.activePreviewAttachments = attachments || [];
    this.selectedIndex = i;
    this.showPreview = true;
  }

  onTaskUploadComplete(event: any) {
    const currentAttachments = this.taskData.attachments || [];
    const newAttachments = Array.isArray(event) ? event : [event];
    this.taskData.attachments = [...currentAttachments, ...newAttachments];
    console.log('Task uploads completed:', this.taskData.attachments);
  }

  removeTaskAttachment(index: number) {
    const attachments = this.taskData.attachments || [];
    if (index >= 0 && index < attachments.length) {
      attachments.splice(index, 1);
    }
  }

  onBugUploadComplete(event: any) {
    const currentAttachments = this.bugData.attachments || [];
    const newAttachments = Array.isArray(event) ? event : [event];
    this.bugData.attachments = [...currentAttachments, ...newAttachments];
    console.log('Bug uploads completed:', this.bugData.attachments);
  }

  removeBugAttachment(index: number) {
    const attachments = this.bugData.attachments || [];
    if (index >= 0 && index < attachments.length) {
      attachments.splice(index, 1);
    }
  }

  onTaskDrop(event: CdkDragDrop<any[]>) {
    if (event.previousIndex === event.currentIndex) return;

    moveItemInArray(this.storyTasks, event.previousIndex, event.currentIndex);
    this.rawStoryTasks = [...this.storyTasks];

    // Save sort order only for items whose position changed
    this.storyTasks.forEach((task, idx) => {
      if (task.sort_order !== idx) {
        task.sort_order = idx;
        this.taskService
          .updateProjectTask({
            task_id: task.task_id,
            where: { task_id: task.task_id },
            sort_order: idx,
            client_id: this.localStorage.getClientId(),
          })
          .subscribe();
      }
    });
  }

  onBugDrop(event: CdkDragDrop<any[]>) {
    if (event.previousIndex === event.currentIndex) return;

    moveItemInArray(this.storyBugs, event.previousIndex, event.currentIndex);
    this.rawStoryBugs = [...this.storyBugs];

    // Save sort order only for items whose position changed
    this.storyBugs.forEach((bug, idx) => {
      if (bug.sort_order !== idx) {
        bug.sort_order = idx;
        this.bugService
          .updateBug({
            bug_id: bug.bug_id, // Root property required by bug validator
            where: { bug_id: bug.bug_id },
            sort_order: idx,
            client_id: this.localStorage.getClientId(),
          })
          .subscribe();
      }
    });
  }

  loadAllProjectTasks() {
    this.loadingAllTasks = true;
    const isAdmin = this.localStorage.checkFullAccessForTableList(
      this.localStorage.getRole(),
    );
    const where: any = { project_id: this.projectId };
    if (!isAdmin) {
      where.assignee_id = this.localStorage.getUserId();
    }

    this.taskService
      .getAllProjectTasks({
        client_id: this.localStorage.getClientId(),
        where: where,
        limit: 1000,
        page: 1,
      })
      .subscribe({
        next: (res: any) => {
          this.allProjectTasks =
            res?.rows || res?.data?.rows || res?.data || res || [];
          this.loadingAllTasks = false;
        },
        error: () => {
          this.loadingAllTasks = false;
          this.toastr.error('Failed to load tasks');
        },
      });
  }

  toggleTaskCompleted(task: any) {
    const isCompleted = task.status === 'completed';
    const newStatus = isCompleted ? 'todo' : 'completed';
    const newProgress = isCompleted ? 0 : 100;

    task.status = newStatus;
    task.progress_percentage = newProgress;

    this.taskService
      .updateProjectTask({
        task_id: task.task_id,
        where: { task_id: task.task_id },
        status: newStatus,
        progress_percentage: newProgress,
        client_id: this.localStorage.getClientId(),
      })
      .subscribe({
        next: () => {
          this.toastr.success('Task status updated');
          if (this.selectedUserStory)
            this.loadStoryTasks(this.selectedUserStory);
          if (this.activeMainTab === 'tasks' && this.tasksChild)
            this.tasksChild.loadTasks();
          if (this.activeMainTab === 'kanban' && this.kanbanChild)
            this.kanbanChild.loadTasks();
        },
        error: () => {
          this.toastr.error('Failed to update task');
        },
      });
  }

  loadAllProjectBugs() {
    this.loadingAllBugs = true;
    this.bugService
      .getAllBugs({
        client_id: this.localStorage.getClientId(),
        where: { project_id: this.projectId },
        limit: 1000,
        page: 1,
      })
      .subscribe({
        next: (res: any) => {
          this.allProjectBugs =
            res?.rows || res?.data?.rows || res?.data || res || [];
          this.loadingAllBugs = false;
        },
        error: () => {
          this.loadingAllBugs = false;
          this.toastr.error('Failed to load bugs');
        },
      });
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  OVERVIEW DASHBOARD
  // ══════════════════════════════════════════════════════════════════════════
  loadOverviewData() {
    this.loadingOverview = true;
    const clientId = this.localStorage.getClientId();
    const where: any = { project_id: this.projectId };

    let loadedCount = 0;
    const checkAndBuild = () => {
      loadedCount++;
      if (loadedCount === 2) {
        this.buildRecentActivity();
        this.buildUpcomingDeadlines();
        this.buildProgressChart();
        this.loadingOverview = false;
      }
    };

    this.taskService
      .getAllProjectTasks({
        client_id: clientId,
        where,
        limit: 1000,
        page: 1,
      })
      .subscribe({
        next: (res: any) => {
          this.allProjectTasks =
            res?.rows || res?.data?.rows || res?.data || res || [];
          checkAndBuild();
        },
        error: () => {
          checkAndBuild();
        },
      });

    this.bugService
      .getAllBugs({
        client_id: clientId,
        where,
        limit: 1000,
        page: 1,
      })
      .subscribe({
        next: (res: any) => {
          this.allProjectBugs =
            res?.rows || res?.data?.rows || res?.data || res || [];
          checkAndBuild();
        },
        error: () => {
          checkAndBuild();
        },
      });
  }

  buildRecentActivity() {
    const activities: any[] = [];
    const tasks = (this.allProjectTasks || []).slice(0, 20);
    tasks.forEach((t: any) => {
      const actorId = t.updated_by || t.created_by || t.assignee_id || t.reporter_id;
      const s = this.normalizeStatusKey(t.status || t.project_task_status);
      activities.push({
        type: 'task',
        icon: 'fa-tasks',
        color: '#6366f1',
        bgColor: '#eef2ff',
        actor: this.getMemberName(actorId, t),
        action:
          s === 'completed' || s === 'done' ? 'completed a task' : 'updated a task',
        subject: t.title,
        time: t.updated_at || t.created_at,
      });
    });
    const bugs = (this.allProjectBugs || []).slice(0, 10);
    bugs.forEach((b: any) => {
      const actorId = b.updated_by || b.created_by || b.reported_by || b.assigned_to || b.tester_id;
      const s = this.normalizeStatusKey(b.status);
      activities.push({
        type: 'bug',
        icon: 'fa-bug',
        color: '#dc2626',
        bgColor: '#fee2e2',
        actor: this.getMemberName(actorId, b),
        action: s === 'closed' || s === 'resolved' ? 'resolved a bug' : 'created a new bug',
        subject: b.title,
        time: b.updated_at || b.updatedAt || b.created_at || b.createdAt,
      });
    });
    activities.sort(
      (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime(),
    );
    this.recentActivity = activities.slice(0, 8);
  }

  buildUpcomingDeadlines() {
    const now = new Date();
    const deadlines = (this.allProjectTasks || [])
      .filter((t: any) => {
        const s = this.normalizeStatusKey(t.status || t.project_task_status);
        return t.due_date && s !== 'completed' && s !== 'done';
      })
      .map((t: any) => ({
        title: t.title,
        due_date: t.due_date,
        priority: t.priority || 'medium',
        status: t.status,
        daysLeft: Math.ceil(
          (new Date(t.due_date).getTime() - now.getTime()) /
            (1000 * 60 * 60 * 24),
        ),
      }))
      .sort((a: any, b: any) => a.daysLeft - b.daysLeft)
      .slice(0, 6);
    this.upcomingDeadlines = deadlines;
  }

  getMemberName(userId: any, item?: any): string {
    // 1. Try finding in projectMembers by user_id
    if (userId) {
      const uid = Number(userId);
      const member = (this.projectMembers || []).find(
        (m: any) => Number(m.user_id) === uid,
      );
      if (member) {
        const name = `${member.prefix || ''} ${member.fname || ''} ${member.lname || ''}`
          .replace(/\s+/g, ' ')
          .trim();
        if (name) return name;
      }
    }

    // 2. Try nested assignee object on item
    if (item?.assignee) {
      const a = item.assignee;
      const name = `${a.prefix || ''} ${a.fname || ''} ${a.lname || ''}`
        .replace(/\s+/g, ' ')
        .trim();
      if (name) return name;
    }

    // 3. Try nested creator/reporter/user object on item
    if (item?.creator || item?.reporter || item?.user || item?.assigned_to_user) {
      const u = item.creator || item.reporter || item.user || item.assigned_to_user;
      const name = `${u.prefix || ''} ${u.fname || ''} ${u.lname || ''}`
        .replace(/\s+/g, ' ')
        .trim();
      if (name) return name;
    }

    // 4. Try current logged-in user if matching
    if (userId && Number(userId) === Number(this.localStorage.getUserId())) {
      const loggedUser = this.localStorage.getUser() || {};
      const name = `${loggedUser.fname || ''} ${loggedUser.lname || ''}`.trim();
      if (name) return name;
    }

    // 5. Check fallback assignee_id on projectMembers
    if (item?.assignee_id) {
      const uid = Number(item.assignee_id);
      const member = (this.projectMembers || []).find(
        (m: any) => Number(m.user_id) === uid,
      );
      if (member) {
        const name = `${member.prefix || ''} ${member.fname || ''} ${member.lname || ''}`
          .replace(/\s+/g, ' ')
          .trim();
        if (name) return name;
      }
    }

    // 6. Fallback
    return 'Team Member';
  }

  getTimeAgo(dateStr: any): string {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return mins <= 1 ? 'just now' : `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  }

  get projectManagerUser(): any {
    if (!this.projectData?.project_manager_id || !this.projectMembers?.length)
      return null;
    return this.projectMembers.find(
      (m) => Number(m.user_id) === Number(this.projectData.project_manager_id),
    );
  }

  normalizeStatusKey(s: any): string {
    if (!s) return '';
    return String(s).toLowerCase().replace(/[\s_-]+/g, '');
  }

  get ovTotalTasks(): number {
    return (this.allProjectTasks || []).length;
  }
  get ovCompletedTasks(): number {
    return (this.allProjectTasks || []).filter((t: any) => {
      const s = this.normalizeStatusKey(t.status || t.project_task_status);
      return s === 'completed' || s === 'done' || s === 'closed' || s === 'resolved';
    }).length;
  }
  get ovInProgressTasks(): number {
    return (this.allProjectTasks || []).filter((t: any) => {
      const s = this.normalizeStatusKey(t.status || t.project_task_status);
      return s === 'inprogress' || s === 'inreview' || s === 'codereview' || s === 'readyfortest' || s === 'assigned';
    }).length;
  }
  get ovToDoTasks(): number {
    return (this.allProjectTasks || []).filter((t: any) => {
      const s = this.normalizeStatusKey(t.status || t.project_task_status);
      return s === 'todo' || s === 'open' || s === 'backlog' || !s;
    }).length;
  }
  get ovInReviewTasks(): number {
    return (this.allProjectTasks || []).filter((t: any) => {
      const s = this.normalizeStatusKey(t.status || t.project_task_status);
      return s === 'inreview' || s === 'codereview';
    }).length;
  }
  get ovBlockedTasks(): number {
    return (this.allProjectTasks || []).filter((t: any) => {
      const s = this.normalizeStatusKey(t.status || t.project_task_status);
      return s === 'blocked';
    }).length;
  }

  get ovTotalBugs(): number {
    return (this.allProjectBugs || []).length;
  }
  get ovOpenBugs(): number {
    return (this.allProjectBugs || []).filter((b: any) => {
      const s = this.normalizeStatusKey(b.status);
      return s === 'open' || s === 'reopen' || s === 'new' || s === 'todo';
    }).length;
  }
  get ovInProgressBugs(): number {
    return (this.allProjectBugs || []).filter((b: any) => {
      const s = this.normalizeStatusKey(b.status);
      return s === 'inprogress' || s === 'assigned' || s === 'inreview' || s === 'readyfortest';
    }).length;
  }
  get ovResolvedBugs(): number {
    return (this.allProjectBugs || []).filter((b: any) => {
      const s = this.normalizeStatusKey(b.status);
      return (
        s === 'closed' ||
        s === 'readyfortest' ||
        s === 'inreview' ||
        s === 'resolved'
      );
    }).length;
  }
  get ovResolvedBugsOnly(): number {
    return (this.allProjectBugs || []).filter(
      (b: any) => this.normalizeStatusKey(b.status) === 'resolved',
    ).length;
  }
  get ovClosedBugs(): number {
    return (this.allProjectBugs || []).filter((b: any) => {
      const s = this.normalizeStatusKey(b.status);
      return s === 'closed' || s === 'resolved' || s === 'rejected' || s === 'completed';
    }).length;
  }
  get ovReopenBugs(): number {
    return (this.allProjectBugs || []).filter((b: any) => {
      const s = this.normalizeStatusKey(b.status);
      return s === 'reopen';
    }).length;
  }
  get ovInReviewBugs(): number {
    return (this.allProjectBugs || []).filter(
      (b: any) => this.normalizeStatusKey(b.status) === 'inreview',
    ).length;
  }
  get ovReadyForTestBugs(): number {
    return (this.allProjectBugs || []).filter(
      (b: any) => this.normalizeStatusKey(b.status) === 'readyfortest',
    ).length;
  }
  get ovRejectedBugs(): number {
    return (this.allProjectBugs || []).filter(
      (b: any) => this.normalizeStatusKey(b.status) === 'rejected',
    ).length;
  }

  get bugStatusDonutSegments() {
    const statuses = [
      { key: 'closed', label: 'Closed', count: this.ovClosedBugs, color: '#16a34a' },
      { key: 'rejected', label: 'Rejected', count: this.ovRejectedBugs, color: '#64748b' },
      { key: 'ready_for_test', label: 'Ready for Test', count: this.ovReadyForTestBugs, color: '#0d9488' },
      { key: 'in_review', label: 'In Review', count: this.ovInReviewBugs, color: '#a855f7' },
      { key: 'in_progress', label: 'In Progress', count: this.ovInProgressBugs, color: '#ea580c' },
      { key: 're-open', label: 'Re-Open', count: this.ovReopenBugs, color: '#dc2626' },
      { key: 'open', label: 'Open', count: this.ovOpenBugs, color: '#2563eb' }
    ];

    let currentOffset = 59.69;
    const circumference = 238.76;

    return statuses.map(s => {
      const pct = this.ovTotalBugs ? (s.count / this.ovTotalBugs) : 0;
      const strokeLength = pct * circumference;
      const offset = currentOffset;
      if (this.ovTotalBugs) {
        currentOffset -= strokeLength;
      }
      return {
        ...s,
        pctVal: Math.round(pct * 100),
        dashArray: `${strokeLength} ${circumference}`,
        dashOffset: offset
      };
    });
  }
  get ovCriticalBugs(): number {
    return (this.allProjectBugs || []).filter((b: any) => String(b.severity).toLowerCase() === 'critical')
      .length;
  }
  get ovActiveMembers(): number {
    return (this.projectMembers || []).length;
  }
  get ovTotalEpics(): number {
    return (this.epics || []).length;
  }
  get ovTotalFeatures(): number {
    let count = 0;
    this.featuresMap.forEach((f) => (count += f.length));
    return count;
  }
  get ovTotalStories(): number {
    let count = 0;
    this.storiesMap.forEach((s) => (count += s.length));
    return count;
  }
  get ovProgress(): number {
    if (!this.ovTotalTasks) return 0;
    return Math.round((this.ovCompletedTasks / this.ovTotalTasks) * 100);
  }
  get ovTaskProgress(): number {
    if (!this.ovTotalTasks) return 0;
    return Math.round((this.ovCompletedTasks / this.ovTotalTasks) * 100);
  }
  get ovBugProgress(): number {
    if (!this.ovTotalBugs) return 0;
    return Math.round((this.ovClosedBugs / this.ovTotalBugs) * 100);
  }
  get ovCombinedTotal(): number {
    return this.ovTotalTasks + this.ovTotalBugs;
  }
  get ovCombinedCompleted(): number {
    return this.ovCompletedTasks + this.ovClosedBugs;
  }
  get ovCombinedProgress(): number {
    if (!this.ovCombinedTotal) return 0;
    return Math.round((this.ovCombinedCompleted / this.ovCombinedTotal) * 100);
  }
  get ovHighPriorityTasks(): number {
    return (this.allProjectTasks || []).filter((t: any) => {
      const p = String(t.priority).toLowerCase();
      return p === 'high' || p === 'urgent';
    }).length;
  }
  get ovOverdueTasks(): number {
    const now = new Date();
    return (this.allProjectTasks || []).filter((t: any) => {
      const s = this.normalizeStatusKey(t.status || t.project_task_status);
      return t.due_date && new Date(t.due_date) < now && s !== 'completed' && s !== 'done';
    }).length;
  }
  get ovHighPriorityBugs(): number {
    return (this.allProjectBugs || []).filter((b: any) => {
      const p = String(b.priority).toLowerCase();
      return p === 'high' || p === 'urgent';
    }).length;
  }
  get ovOverdueBugs(): number {
    const now = new Date();
    return (this.allProjectBugs || []).filter((b: any) => {
      const s = this.normalizeStatusKey(b.status);
      return b.due_date && new Date(b.due_date) < now && s !== 'closed';
    }).length;
  }
  get ovBugResolutionRate(): number {
    if (!this.ovTotalBugs) return 0;
    return Math.round((this.ovResolvedBugs / this.ovTotalBugs) * 100);
  }

  getTaskCountForMember(userId: any): number {
    const uid = Number(userId);
    if (!uid) return 0;
    return (this.allProjectTasks || []).filter(
      (t: any) =>
        Number(t.assignee_id) === uid ||
        (Array.isArray(t.assignee_ids) &&
          t.assignee_ids.some((id: any) => Number(id) === uid)) ||
        (t.assignees && t.assignees.some((a: any) => Number(a.user_id) === uid)),
    ).length;
  }

  // ── Priority & Status bar chart data ────────────────────────────────────────
  get taskPriorityBars() {
    const urgent = this.allProjectTasks.filter(
      (t: any) => t.priority === 'urgent',
    ).length;
    const high = this.allProjectTasks.filter(
      (t: any) => t.priority === 'high',
    ).length;
    const medium = this.allProjectTasks.filter(
      (t: any) => t.priority === 'medium',
    ).length;
    const low = this.allProjectTasks.filter(
      (t: any) => t.priority === 'low',
    ).length;
    const max = Math.max(urgent, high, medium, low, 1);
    return [
      {
        label: 'Urgent',
        count: urgent,
        pct: Math.round((urgent / max) * 100),
        color: '#7c3aed',
        bg: '#f5f3ff',
      },
      {
        label: 'High',
        count: high,
        pct: Math.round((high / max) * 100),
        color: '#dc2626',
        bg: '#fee2e2',
      },
      {
        label: 'Medium',
        count: medium,
        pct: Math.round((medium / max) * 100),
        color: '#d97706',
        bg: '#fef3c7',
      },
      {
        label: 'Low',
        count: low,
        pct: Math.round((low / max) * 100),
        color: '#059669',
        bg: '#d1fae5',
      },
    ];
  }

  get bugPriorityBars() {
    const critical = this.allProjectBugs.filter(
      (b: any) => b.severity === 'critical',
    ).length;
    const high = this.allProjectBugs.filter(
      (b: any) => b.severity === 'high' || b.priority === 'high',
    ).length;
    const medium = this.allProjectBugs.filter(
      (b: any) => b.severity === 'medium' || b.priority === 'medium',
    ).length;
    const low = this.allProjectBugs.filter(
      (b: any) => b.severity === 'low' || b.priority === 'low',
    ).length;
    const max = Math.max(critical, high, medium, low, 1);
    return [
      {
        label: 'Critical',
        count: critical,
        pct: Math.round((critical / max) * 100),
        color: '#991b1b',
        bg: '#fee2e2',
      },
      {
        label: 'High',
        count: high,
        pct: Math.round((high / max) * 100),
        color: '#dc2626',
        bg: '#ffedd5',
      },
      {
        label: 'Medium',
        count: medium,
        pct: Math.round((medium / max) * 100),
        color: '#d97706',
        bg: '#fef3c7',
      },
      {
        label: 'Low',
        count: low,
        pct: Math.round((low / max) * 100),
        color: '#059669',
        bg: '#d1fae5',
      },
    ];
  }

  get combinedStatusBars() {
    const statuses = [
      {
        label: 'To Do',
        color: '#94a3b8',
        taskCount: this.ovToDoTasks,
        bugCount: this.ovOpenBugs,
      },
      {
        label: 'In Progress',
        color: '#6366f1',
        taskCount: this.ovInProgressTasks,
        bugCount: this.ovInProgressBugs,
      },
      {
        label: 'In Review',
        color: '#a855f7',
        taskCount: this.ovInReviewTasks,
        bugCount: 0,
      },
      {
        label: 'Completed',
        color: '#10b981',
        taskCount: this.ovCompletedTasks,
        bugCount: this.ovResolvedBugs,
      },
    ];
    const max = Math.max(...statuses.map((s) => s.taskCount + s.bugCount), 1);
    return statuses.map((s) => ({
      ...s,
      total: s.taskCount + s.bugCount,
      taskPct: Math.round((s.taskCount / max) * 100),
      bugPct: Math.round((s.bugCount / max) * 100),
    }));
  }

  // ── Progress Chart ──────────────────────────────────────────────────────────
  buildProgressChart() {
    const now = new Date();
    const weeks: {
      label: string;
      shortLabel: string;
      taskPct: number;
      bugPct: number;
      taskCompleted: number;
      taskTotal: number;
      bugCompleted: number;
      bugTotal: number;
    }[] = [];
    const totalTasks = this.allProjectTasks.length;
    const totalBugs = this.allProjectBugs.length;

    for (let i = 7; i >= 0; i--) {
      const weekEnd = new Date(now);
      weekEnd.setDate(now.getDate() - i * 7);
      weekEnd.setHours(23, 59, 59, 999);

      // Tasks
      const tasksCreatedByDate = this.allProjectTasks.filter(
        (t: any) => new Date(t.created_at) <= weekEnd,
      ).length;
      const tasksCompletedByDate = this.allProjectTasks.filter(
        (t: any) =>
          t.status === 'completed' &&
          new Date(t.updated_at || t.created_at) <= weekEnd,
      ).length;
      const taskPct = Math.min(100, Math.round((tasksCompletedByDate / (totalTasks || 1)) * 100));

      // Bugs
      const bugsCreatedByDate = this.allProjectBugs.filter(
        (b: any) => new Date(b.created_at || b.createdAt) <= weekEnd,
      ).length;
      const bugsCompletedByDate = this.allProjectBugs.filter(
        (b: any) =>
          (b.status === 'closed' || b.status === 'resolved') &&
          new Date(b.updated_at || b.updatedAt || b.created_at || b.createdAt) <= weekEnd,
      ).length;
      const bugPct = Math.min(100, Math.round((bugsCompletedByDate / (totalBugs || 1)) * 100));

      const label = weekEnd.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      const shortLabel = weekEnd.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });

      weeks.push({
        label,
        shortLabel,
        taskPct,
        bugPct,
        taskCompleted: tasksCompletedByDate,
        taskTotal: tasksCreatedByDate,
        bugCompleted: bugsCompletedByDate,
        bugTotal: bugsCreatedByDate,
      });
    }

    this.progressChartWeeks = weeks;
  }

  // SVG chart dimensions
  private readonly CHART_W = 560;
  private readonly CHART_H = 150;
  private readonly CHART_PAD_X = 30;
  private readonly CHART_PAD_Y = 16;

  private getChartX(i: number): number {
    const n = this.progressChartWeeks.length;
    if (n <= 1) return this.CHART_PAD_X;
    return (
      this.CHART_PAD_X + (i / (n - 1)) * (this.CHART_W - this.CHART_PAD_X * 2)
    );
  }

  private getChartY(pct: number): number {
    return (
      this.CHART_PAD_Y + (1 - pct / 100) * (this.CHART_H - this.CHART_PAD_Y * 2)
    );
  }

  get taskLinePath(): string {
    if (!this.progressChartWeeks.length) return '';
    return this.progressChartWeeks
      .map(
        (w, i) =>
          `${i === 0 ? 'M' : 'L'} ${this.getChartX(i)},${this.getChartY(w.taskPct)}`,
      )
      .join(' ');
  }

  get taskAreaPath(): string {
    if (!this.progressChartWeeks.length) return '';
    const pts = this.progressChartWeeks
      .map(
        (w, i) =>
          `${i === 0 ? 'M' : 'L'} ${this.getChartX(i)},${this.getChartY(w.taskPct)}`,
      )
      .join(' ');
    const n = this.progressChartWeeks.length;
    const lastX = this.getChartX(n - 1);
    const firstX = this.getChartX(0);
    const bottomY = this.CHART_H - this.CHART_PAD_Y;
    return `${pts} L ${lastX},${bottomY} L ${firstX},${bottomY} Z`;
  }

  get bugLinePath(): string {
    if (!this.progressChartWeeks.length) return '';
    return this.progressChartWeeks
      .map(
        (w, i) =>
          `${i === 0 ? 'M' : 'L'} ${this.getChartX(i)},${this.getChartY(w.bugPct)}`,
      )
      .join(' ');
  }

  get bugAreaPath(): string {
    if (!this.progressChartWeeks.length) return '';
    const pts = this.progressChartWeeks
      .map(
        (w, i) =>
          `${i === 0 ? 'M' : 'L'} ${this.getChartX(i)},${this.getChartY(w.bugPct)}`,
      )
      .join(' ');
    const n = this.progressChartWeeks.length;
    const lastX = this.getChartX(n - 1);
    const firstX = this.getChartX(0);
    const bottomY = this.CHART_H - this.CHART_PAD_Y;
    return `${pts} L ${lastX},${bottomY} L ${firstX},${bottomY} Z`;
  }

  get chartDots(): {
    x: number;
    yTask: number;
    yBug: number;
    taskPct: number;
    bugPct: number;
    label: string;
    shortLabel: string;
  }[] {
    return this.progressChartWeeks.map((w, i) => ({
      x: this.getChartX(i),
      yTask: this.getChartY(w.taskPct),
      yBug: this.getChartY(w.bugPct),
      taskPct: w.taskPct,
      bugPct: w.bugPct,
      label: w.label,
      shortLabel: w.shortLabel,
    }));
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  TIMELINE & GANTT METHODS
  // ══════════════════════════════════════════════════════════════════════════
  get timelineDays(): number[] {
    const year = this.timelineDate.getFullYear();
    const month = this.timelineDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  }

  get timelineMonthLabel(): string {
    return this.timelineDate.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  }

  prevTimelineMonth() {
    this.timelineDate = new Date(
      this.timelineDate.getFullYear(),
      this.timelineDate.getMonth() - 1,
      1,
    );
  }

  nextTimelineMonth() {
    this.timelineDate = new Date(
      this.timelineDate.getFullYear(),
      this.timelineDate.getMonth() + 1,
      1,
    );
  }

  goToToday() {
    this.timelineDate = new Date();
  }

  isToday(day: number): boolean {
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === this.timelineDate.getMonth() &&
      today.getFullYear() === this.timelineDate.getFullYear()
    );
  }

  get todayLineLeft(): number {
    const today = new Date();
    if (
      today.getMonth() === this.timelineDate.getMonth() &&
      today.getFullYear() === this.timelineDate.getFullYear()
    ) {
      const daysInMonth = this.timelineDays.length;
      return (
        ((today.getDate() - 1 + today.getHours() / 24) / daysInMonth) * 100
      );
    }
    return -1;
  }

  getItemDates(
    item: any,
    type: string,
  ): { start: Date; due: Date; isFallback: boolean } {
    let start: Date | null = null;
    let due: Date | null = null;
    let isFallback = false;

    if (type === 'task') {
      start = item.start_date ? new Date(item.start_date) : null;
      due = item.due_date ? new Date(item.due_date) : null;
    } else if (type === 'bug') {
      start = (item.created_at || item.createdAt) ? new Date(item.created_at || item.createdAt) : null;
      due = item.due_date ? new Date(item.due_date) : null;
    } else if (type === 'story') {
      const tasks = item.tasks || [];
      const bugs = item.bugs || [];
      const children = [...tasks, ...bugs];
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        const cType = child.task_id ? 'task' : 'bug';
        const d = this.getItemDates(child, cType);
        if (!d.isFallback) {
          if (start === null) {
            start = d.start;
          } else if (d.start.getTime() < start.getTime()) {
            start = d.start;
          }

          if (due === null) {
            due = d.due;
          } else if (d.due.getTime() > due.getTime()) {
            due = d.due;
          }
        }
      }
    } else if (type === 'feature') {
      start = item.start_date ? new Date(item.start_date) : null;
      due = item.due_date ? new Date(item.due_date) : null;
      if (!start || !due) {
        const stories =
          this.storiesMap.get(item.feature_id) || item.stories || [];
        for (let i = 0; i < stories.length; i++) {
          const story = stories[i];
          const d = this.getItemDates(story, 'story');
          if (!d.isFallback) {
            if (start === null) {
              start = d.start;
            } else if (d.start.getTime() < start.getTime()) {
              start = d.start;
            }

            if (due === null) {
              due = d.due;
            } else if (d.due.getTime() > due.getTime()) {
              due = d.due;
            }
          }
        }
      }
    } else if (type === 'epic') {
      start = item.start_date ? new Date(item.start_date) : null;
      due = item.due_date ? new Date(item.due_date) : null;
      if (!start || !due) {
        const features =
          this.featuresMap.get(item.epic_id) || item.features || [];
        for (let i = 0; i < features.length; i++) {
          const feat = features[i];
          const d = this.getItemDates(feat, 'feature');
          if (!d.isFallback) {
            if (start === null) {
              start = d.start;
            } else if (d.start.getTime() < start.getTime()) {
              start = d.start;
            }

            if (due === null) {
              due = d.due;
            } else if (d.due.getTime() > due.getTime()) {
              due = d.due;
            }
          }
        }
      }
    }

    if (!start || !due) {
      isFallback = true;
      start = (item.created_at || item.createdAt) ? new Date(item.created_at || item.createdAt) : new Date();
      due = item.due_date
        ? new Date(item.due_date)
        : new Date(start.getTime() + 3 * 24 * 60 * 60 * 1000);
    }

    return { start, due, isFallback };
  }

  getItemBarStyle(item: any, type: string): any {
    const dates = this.getItemDates(item, type);
    const year = this.timelineDate.getFullYear();
    const month = this.timelineDate.getMonth();
    const monthStart = new Date(year, month, 1, 0, 0, 0, 0);
    const monthEnd = new Date(year, month + 1, 0, 23, 59, 59, 999);

    const startMs = dates.start.getTime();
    const dueMs = dates.due.getTime();
    const mStartMs = monthStart.getTime();
    const mEndMs = monthEnd.getTime();

    // If completely outside the current month
    if (dueMs < mStartMs || startMs > mEndMs) {
      return { display: 'none' };
    }

    const totalMs = mEndMs - mStartMs;
    const clampedStart = Math.max(mStartMs, startMs);
    const clampedEnd = Math.min(mEndMs, dueMs);

    const left = ((clampedStart - mStartMs) / totalMs) * 100;
    const width = ((clampedEnd - clampedStart) / totalMs) * 100;

    return {
      left: `${left}%`,
      width: `${Math.max(2, width)}%`,
    };
  }

  getItemDateRangeLabel(item: any, type: string): string {
    const dates = this.getItemDates(item, type);
    const startStr = dates.start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    const dueStr = dates.due.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    return (
      `${startStr} - ${dueStr}` + (dates.isFallback ? ' (Unscheduled)' : '')
    );
  }

  toggleTimelineEpic(epicId: number) {
    if (this.expandedEpics.has(epicId)) {
      this.expandedEpics.delete(epicId);
    } else {
      this.expandedEpics.add(epicId);
    }
  }

  toggleTimelineFeature(featId: number) {
    if (this.expandedFeatures.has(featId)) {
      this.expandedFeatures.delete(featId);
    } else {
      this.expandedFeatures.add(featId);
    }
  }

  toggleTimelineStory(storyId: number) {
    if (this.expandedStories.has(storyId)) {
      this.expandedStories.delete(storyId);
    } else {
      this.expandedStories.add(storyId);
    }
  }

  getTimelineRows(): any[] {
    const rows: any[] = [];
    const sf = this.timelineSprintFilter || 'all';

    this.epics.forEach((epic: any) => {
      // Collect matching features
      const features = this.featuresMap.get(epic.epic_id) || epic.features || [];
      const matchingFeatures: any[] = [];

      features.forEach((feat: any) => {
        // Collect matching stories
        const stories = this.storiesMap.get(feat.feature_id) || feat.stories || [];
        const matchingStories: any[] = [];

        stories.forEach((story: any) => {
          // Filter tasks
          let tasks = story.tasks || [];
          if (sf === 'backlog') {
            tasks = tasks.filter((t: any) => !t.sprint_id || Number(t.sprint_id) === 0);
          } else if (sf !== 'all') {
            tasks = tasks.filter((t: any) => Number(t.sprint_id) === Number(sf));
          }

          // Filter bugs
          let bugs = story.bugs || [];
          if (sf === 'backlog') {
            bugs = bugs.filter((b: any) => !b.sprint_id || Number(b.sprint_id) === 0);
          } else if (sf !== 'all') {
            bugs = bugs.filter((b: any) => Number(b.sprint_id) === Number(sf));
          }

          // Does story match sprint filter?
          let storyMatches = false;
          if (sf === 'all') {
            storyMatches = true;
          } else if (sf === 'backlog') {
            storyMatches = !story.sprint_id;
          } else {
            storyMatches = Number(story.sprint_id) === Number(sf);
          }

          // If the story matches, or any of its tasks/bugs match, we show the story
          if (storyMatches || tasks.length > 0 || bugs.length > 0) {
            matchingStories.push({
              story,
              filteredTasks: tasks,
              filteredBugs: bugs
            });
          }
        });

        // If feat has matching stories or features should always be shown in 'all' mode
        if (matchingStories.length > 0 || sf === 'all') {
          matchingFeatures.push({
            feat,
            matchingStories
          });
        }
      });

      // If epic has matching features or epics should always be shown in 'all' mode
      if (matchingFeatures.length > 0 || sf === 'all') {
        // Push epic row
        rows.push({
          id: epic.epic_id,
          title: epic.name,
          type: 'epic',
          level: 0,
          expanded: this.expandedEpics.has(epic.epic_id),
          original: epic,
        });

        if (this.expandedEpics.has(epic.epic_id)) {
          matchingFeatures.forEach(({ feat, matchingStories }: any) => {
            // Push feature row
            rows.push({
              id: feat.feature_id,
              title: feat.name,
              type: 'feature',
              level: 1,
              expanded: this.expandedFeatures.has(feat.feature_id),
              original: feat,
            });

            if (this.expandedFeatures.has(feat.feature_id)) {
              matchingStories.forEach(({ story, filteredTasks, filteredBugs }: any) => {
                // Push story row
                rows.push({
                  id: story.user_story_id,
                  title: story.title,
                  type: 'story',
                  level: 2,
                  expanded: this.expandedStories.has(story.user_story_id),
                  original: story,
                });

                if (this.expandedStories.has(story.user_story_id)) {
                  filteredTasks.forEach((task: any) => {
                    rows.push({
                      id: task.task_id,
                      title: task.title,
                      type: 'task',
                      level: 3,
                      original: task,
                    });
                  });

                  filteredBugs.forEach((bug: any) => {
                    rows.push({
                      id: bug.bug_id,
                      title: bug.title || bug.bug_title || 'Unnamed Bug',
                      type: 'bug',
                      level: 3,
                      original: bug,
                    });
                  });
                }
              });
            }
          });
        }
      }
    });

    return rows;
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  CALENDAR METHODS
  // ══════════════════════════════════════════════════════════════════════════
  get calendarMonthLabel(): string {
    return this.calendarDate.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  }

  prevCalendarMonth() {
    this.calendarDate = new Date(
      this.calendarDate.getFullYear(),
      this.calendarDate.getMonth() - 1,
      1,
    );
  }

  nextCalendarMonth() {
    this.calendarDate = new Date(
      this.calendarDate.getFullYear(),
      this.calendarDate.getMonth() + 1,
      1,
    );
  }

  goToCalendarToday() {
    this.calendarDate = new Date();
  }

  getCalendarWeeks(): (Date | null)[][] {
    const year = this.calendarDate.getFullYear();
    const month = this.calendarDate.getMonth();
    const firstDayIndex = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days: (Date | null)[] = [];
    for (let i = 0; i < firstDayIndex; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    while (days.length % 7 !== 0) {
      days.push(null);
    }

    const weeks: (Date | null)[][] = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7) as (Date | null)[]);
    }
    return weeks;
  }

  getCalendarItemsForDay(date: Date | null): any[] {
    if (!date) return [];
    const dStart = new Date(date);
    dStart.setHours(0, 0, 0, 0);
    const dEnd = new Date(date);
    dEnd.setHours(23, 59, 59, 999);
    const sf = this.calendarSprintFilter || 'all';

    let filteredTasks = this.allProjectTasks || [];
    if (sf === 'backlog') {
      filteredTasks = filteredTasks.filter((t: any) => !t.sprint_id || Number(t.sprint_id) === 0);
    } else if (sf !== 'all') {
      filteredTasks = filteredTasks.filter((t: any) => Number(t.sprint_id) === Number(sf));
    }

    let filteredBugs = this.allProjectBugs || [];
    if (sf === 'backlog') {
      filteredBugs = filteredBugs.filter((b: any) => !b.sprint_id || Number(b.sprint_id) === 0);
    } else if (sf !== 'all') {
      filteredBugs = filteredBugs.filter((b: any) => Number(b.sprint_id) === Number(sf));
    }

    const tasks = filteredTasks
      .filter((t: any) => {
        const dates = this.getItemDates(t, 'task');
        return (
          dates.start &&
          dates.due &&
          ((dates.start >= dStart && dates.start <= dEnd) ||
            (dates.due >= dStart && dates.due <= dEnd) ||
            (dates.start <= dStart && dates.due >= dEnd))
        );
      })
      .map((t) => ({ ...t, _itemType: 'task' }));

    const bugs = filteredBugs
      .filter((b: any) => {
        const dates = this.getItemDates(b, 'bug');
        return (
          dates.start &&
          dates.due &&
          ((dates.start >= dStart && dates.start <= dEnd) ||
            (dates.due >= dStart && dates.due <= dEnd) ||
            (dates.start <= dStart && dates.due >= dEnd))
        );
      })
      .map((b) => ({ ...b, _itemType: 'bug' }));

    return [...tasks, ...bugs];
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  SETTINGS METHODS
  // ══════════════════════════════════════════════════════════════════════════
  initSettingsForm() {
    if (this.projectData) {
      this.settingsName = this.projectData.name || '';
      this.settingsDesc = this.projectData.description || '';
      this.settingsStatus = this.projectData.status || 'todo';
      this.settingsManagerId = this.projectData.project_manager_id || null;
      this.settingsStartDate = this.projectData.start_date
        ? this.projectData.start_date.split('T')[0]
        : '';
      this.settingsDueDate = this.projectData.due_date
        ? this.projectData.due_date.split('T')[0]
        : '';
    }
  }

  updateProjectSettings() {
    if (!this.settingsName.trim()) {
      this.toastr.warning('Project Name is required');
      return;
    }
    this.settingsSaving = true;
    this.projectService
      .updateProject({
        project_id: this.projectId,
        client_id: this.localStorage.getClientId(),
        name: this.settingsName,
        description: this.settingsDesc,
        status: this.settingsStatus,
        project_manager_id: this.settingsManagerId,
        start_date: this.settingsStartDate || null,
        due_date: this.settingsDueDate || null,
        where: { project_id: this.projectId },
      })
      .subscribe({
        next: () => {
          this.toastr.success('Project settings updated successfully');
          this.loadProject();
          this.settingsSaving = false;
        },
        error: (err) => {
          this.settingsSaving = false;
          this.toastr.error(
            err?.error?.message || 'Failed to update project settings',
          );
        },
      });
  }

  deleteProjectConfirm() {
    if (
      !confirm(
        `WARNING: Are you sure you want to permanently delete the project "${this.projectData?.name}"? This action cannot be undone and will delete all epics, features, user stories, tasks, and bugs!`,
      )
    ) {
      return;
    }
    this.projectService
      .deleteProject({
        project_id: this.projectId,
        client_id: this.localStorage.getClientId(),
        where: { project_id: this.projectId },
      })
      .subscribe({
        next: () => {
          this.toastr.success('Project deleted successfully');
          this.router.navigate(['/main/manage-projects']);
        },
        error: () => {
          this.toastr.error('Failed to delete project');
        },
      });
  }

  get memberWorkloads(): any[] {
    const maxTasks = Math.max(
      ...(this.projectMembers || []).map((m) =>
        this.getTaskCountForMember(m.user_id),
      ),
      1,
    );
    return (this.projectMembers || [])
      .map((m) => {
        const count = this.getTaskCountForMember(m.user_id);
        return {
          name:
            `${m.fname || ''} ${m.lname || ''}`.trim() || m.email || 'Member',
          count: count,
          pct: Math.round((count / maxTasks) * 100),
          avatarColor: this.getAvatarColor(m),
          initials: this.getInitials(m),
        };
      })
      .sort((a, b) => b.count - a.count);
  }

  get bugSeverityBreakdown() {
    const critical = this.allProjectBugs.filter(
      (b: any) => b.severity === 'critical' || b.severity === 'blocker',
    ).length;
    const high = this.allProjectBugs.filter(
      (b: any) => b.severity === 'high',
    ).length;
    const medium = this.allProjectBugs.filter(
      (b: any) => b.severity === 'medium',
    ).length;
    const low = this.allProjectBugs.filter(
      (b: any) => b.severity === 'low',
    ).length;
    const total = critical + high + medium + low || 1;
    return [
      {
        label: 'Critical/Blocker',
        count: critical,
        pct: Math.round((critical / total) * 100),
        color: '#dc2626',
        bg: '#fee2e2',
      },
      {
        label: 'High Severity',
        count: high,
        pct: Math.round((high / total) * 100),
        color: '#ea580c',
        bg: '#ffedd5',
      },
      {
        label: 'Medium Severity',
        count: medium,
        pct: Math.round((medium / total) * 100),
        color: '#eab308',
        bg: '#fef9c3',
      },
      {
        label: 'Low Severity',
        count: low,
        pct: Math.round((low / total) * 100),
        color: '#3b82f6',
        bg: '#dbeafe',
      },
    ];
  }
}
